/* eslint-disable @typescript-eslint/no-explicit-any */
import { createClient } from '@supabase/supabase-js';
import {
  validateEmail,
  validatePassword,
  validateUsername,
  validateName,
  validateCompany,
  validateJobRole,
  validateUrl,
  validateTextArea
} from '../utils/validations';
import { validStages, CDMO_ENUM, CDMO_STATUS, CDMOBusinessProfile, type CDMOFacility } from '../constants';
import { buildCdmoApplicationRow } from './cdmoApplicationRow';
import type { CdmoImportAuditRow } from './cdmoImport/importRows';
import { findWebsiteCandidates, type CdmoWebsiteRecord } from './cdmoImport/existingDomains';
import {
  validateCdmoApplicationData,
  type CdmoApplicationValidationMode,
  type CdmoApplicationValidationOptions,
} from './cdmoApplicationValidation';
import { VALIDATION_MESSAGES } from '../constants/messages';
import { CDMO_OWNERSHIP_CONSTANTS } from '../constants/cdmoOwnership';
import {
  SharedFieldSettings,
  SharedProjectBrief,
  buildLegacySafeSharedBrief,
  getSponsorDisplayName,
  toCdmoSafeIntroductionRequest,
  type CdmoIntroductionRequestDbRow,
} from './warmIntroSharing';
import { extractEdgeFunctionError } from '../utils/edgeFunctionError';
import {
  buildCdmoBatchHistoryMetrics,
  buildCampaignHistoryIndex,
  buildSponsorHistoryMetrics,
  CAMPAIGN_HISTORY_DEFAULT_PAGE_SIZE,
  CAMPAIGN_HISTORY_EMAIL_IN_CHUNK_SIZE,
  cdmoClaimRecipientEmail,
  chunkStrings,
  collectDistinctCdmoBatchStarts,
  deriveCdmoBatchCampaignStatus,
  deriveSponsorCampaignStatus,
  filterCampaignHistoryIndex,
  groupCdmoProfilesByCampaignStart,
  maxEmailStage,
  paginateCampaignHistoryIndex,
  type CampaignHistoryIndexEntry,
  type CampaignHistoryTypeFilter,
} from './campaignHistory';


const supabaseUrl = (import.meta as any).env?.VITE_SUPABASE_URL || process.env.VITE_SUPABASE_URL;
const supabaseAnonKey = (import.meta as any).env?.VITE_SUPABASE_ANON_KEY || process.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables');
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey);

// Type definitions for our database tables
export interface StartupSignup {
  id?: string;
  name: string;
  email: string;
  company: string;
  role: string;
  development_stage: string[];
  looking_for: string[];
  challenge?: string;
  created_at?: string;
}

export interface CDMOApplication {
  id?: string;
  user_id?: string;
  company_name: string;
  contact_name: string;
  email: string;
  phone: string;
  website?: string;
  fei_number?: string; // Comma-separated FEI numbers for FDA lookups (up to 10)
  primary_capabilities: string[];
  detailed_capabilities: Record<string, string[]>;
  typical_batch_sizes: string[];
  target_client_types: string[];
  engagement_scales: string[];
  preferred_project_types: string[];
  gmp_certifications: string[];
  facility_locations_details?: string;
  /**
   * Optional per-region facility records: location, capabilities available there, and equipment.
   * Informational only — never a matching or scoring input.
   */
  facilities?: CDMOFacility[];
  budget_ranges: string[];
  operating_regions: string[];
  speed_to_start?: string;
  willingness_feasibility_studies: boolean;
  startup_friendly: boolean;
  description?: string;
  file_name?: string;
  created_at?: string;
  // New Admin fields
  status?: CDMO_ENUM;
  cdmo_business_profile?: CDMOBusinessProfile;
  primary_email?: string;
  secondary_emails?: string[];
  admin_notes?: string;
  normalized_domain?: string | null;
  is_admin_created?: boolean;
  claim_token?: string | null;
  claim_token_expires_at?: string | null;
  admin_claim_token?: string | null;
  /** Admin-verified field keys; when set, CDMO users cannot edit these fields */
  locked_fields?: string[];
  /** Admin-triggered claim email campaign (separate from reminder_cycle) */
  campaign_active?: boolean;
  claim_email_stage?: number;
  last_email_sent_at?: string | null;
  claim_campaign_started_at?: string | null;
}

export interface ContactMessage {
  id?: string;
  name: string;
  email: string;
  company?: string;
  reason: string;
  message: string;
  created_at?: string;
}

// User interface for the new users table
export interface User {
  id: string;
  name: string;
  email: string;
  username: string;
  company: string;
  role: string; // 'startup' or 'cdmo'
  job_role: string; // Job title like 'CEO', 'CMC Director', etc.
  development_stage?: string[];
  looking_for: string[];
  challenge?: string;
  website?: string;
  created_at?: string;
}

// Introduction request interface
export interface IntroductionRequest {
  id?: string;
  requester_user_id: string;
  requester_name: string;
  requester_email: string;
  cdmo_id: string;
  request_details: Record<string, any>;
  shared_field_settings?: SharedFieldSettings;
  shared_project_brief?: SharedProjectBrief;
  sponsor_display_name?: string;
  status?: string;
  created_at?: string;
}

/**
 * CDMO-safe introduction request shape exposed to CDMO clients.
 * Deliberately excludes requester identity fields and raw request payload.
 */
export interface CdmoSafeIntroductionRequest {
  id?: string;
  requester_user_id: string;
  cdmo_id: string;
  status?: string;
  created_at?: string;
  shared_field_settings: SharedFieldSettings | null;
  shared_project_brief: SharedProjectBrief;
  sponsor_display_name: string;
}

/** Expert guidance request payload (sponsor-side, Find CDMOs page). */
export type ExpertGuidanceHelpType =
  | 'cdmo_selection'
  | 'supplier_sourcing'
  | 'project_guidance'
  | 'execution_support'
  | 'other';

export interface ExpertGuidanceRequestPayload {
  sponsorName?: string;
  sponsorEmail?: string;
  sponsorCompany?: string;
  helpTypes: ExpertGuidanceHelpType[];
  otherHelpText?: string;
  projectCompanyName?: string;
  briefDescription?: string;
  assistanceTimeline?: string;
  preferredFollowUpEmail?: string;
  submittedFromPage?: string;
}

export interface ExpertGuidanceRequest {
  id?: string;
  user_id?: string | null;
  sponsor_name?: string | null;
  sponsor_email?: string | null;
  sponsor_company?: string | null;
  help_types: string[];
  other_help_text?: string | null;
  project_company_name?: string | null;
  brief_description?: string | null;
  assistance_timeline?: string | null;
  preferred_follow_up_email?: string | null;
  submitted_from_page?: string | null;
  created_at?: string;
  updated_at?: string;
}

/**
 * Reads the JSON error body from a failed edge function invoke (Supabase sets `error` on non-2xx).
 */
async function parseExpertGuidanceFunctionError(error: unknown): Promise<string | null> {
  if (!error || typeof error !== 'object' || !('context' in error)) {
    return null;
  }
  const context = (error as { context?: Response }).context;
  if (!context || typeof context.json !== 'function') {
    return null;
  }
  try {
    const body = (await context.json()) as { error?: string; message?: string };
    return body?.error ?? body?.message ?? null;
  } catch {
    return null;
  }
}

// Review interface
export interface Review {
  id?: string;
  introduction_request_id: string;
  reviewer_user_id: string;
  reviewed_user_id?: string;
  reviewed_cdmo_id?: string;
  reviewer_type: 'startup' | 'cdmo';
  overall_rating: number;
  responsiveness_rating: number;
  communication_rating: number;
  alignment_rating?: number;
  professionalism_rating?: number;
  requirements_clarity_rating?: number;
  text_feedback?: string;
  created_at?: string;
}
// User matching weights interface
export interface UserMatchingWeights {
  user_id: string;
  company_name?: number;
  description?: number;
  facility_locations_details?: number;
  primary_capabilities?: number;
  gmp_certifications?: number;
  budget?: number;
  operating_regions?: number;
  preferred_project_types?: number;
  engagement_scales?: number;
  typical_batch_sizes?: number;
  target_client_types?: number;
  detailed_capabilities?: number;
  speed_to_start?: number;
  created_at?: string;
  updated_at?: string;
}

export interface ScoringBaseWeight {
  criteria: string;
  weight: number;
}

// Add these interfaces after the existing interfaces
export interface Conversation {
  id: string;
  introduction_request_id: string;
  startup_user_id: string;
  cdmo_user_id: string;
  cdmo_id: string;
  created_at: string;
  updated_at: string;
}

export interface Message {
  id: string;
  conversation_id: string;
  sender_id: string;
  content: string;
  is_read: boolean;
  read_at: string | null;
  created_at: string;
}

export interface ConversationWithDetails extends Conversation {
  cdmo_applications?: {
    company_name: string;
    contact_name: string;
  };
  users_startup?: {
    name: string;
    company: string;
  };
  users_cdmo?: {
    name: string;
    company: string;
  };
  introduction_request?: {
    shared_field_settings?: SharedFieldSettings | null;
    shared_project_brief?: SharedProjectBrief | null;
  } | null;
  sponsor_display_name?: string;
  messages?: Message[];
  unread_count?: number;
  last_message?: Message | null;
}

// Enhanced validation service
export const validationService = {
  // Validate user signup data
  validateUserSignup(data: Partial<User>): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];

    // Validate name
    const nameValidation = validateName(data.name || '');
    if (!nameValidation.isValid) {
      errors.push(nameValidation.error!);
    }

    // Validate email
    const emailValidation = validateEmail(data.email || '');
    if (!emailValidation.isValid) {
      errors.push(emailValidation.error!);
    }

    // Validate username
    const usernameValidation = validateUsername(data.username || '');
    if (!usernameValidation.isValid) {
      errors.push(usernameValidation.error!);
    }

    // Validate company
    const companyValidation = validateCompany(data.company || '');
    if (!companyValidation.isValid) {
      errors.push(companyValidation.error!);
    }

    // Validate job role
    const jobRoleValidation = validateJobRole(data.job_role || '');
    if (!jobRoleValidation.isValid) {
      errors.push(jobRoleValidation.error!);
    }

    // Validate role
    if (!data.role || !['startup', 'cdmo'].includes(data.role)) {
      errors.push(VALIDATION_MESSAGES.ROLE_REQUIRED);
    }

    // Validate development stage (optional; stored as text[] — each element must be a known stage)
    if (data.role === 'startup' && Array.isArray(data.development_stage) && data.development_stage.length > 0) {
      const invalidStage = data.development_stage.find((stage) => !validStages.includes(stage));
      if (invalidStage) {
        errors.push(VALIDATION_MESSAGES.DEVELOPMENT_STAGE_REQUIRED);
      }
    }

    // Validate website (only for CDMO)
    if (data.role === 'cdmo') {
      const urlValidation = validateUrl(data.website || '', true);
      if (!urlValidation.isValid) {
        errors.push(urlValidation.error!);
      }
    }


    return {
      isValid: errors.length === 0,
      errors
    };
  },

  // Validate CDMO application data. `mode: 'import'` is the admin CSV bulk upload; see
  // validateCdmoApplicationData for the four checks it relaxes. Omitted, it is the form's rules.
  validateCDMOApplication(
    data: Partial<CDMOApplication>,
    options: CdmoApplicationValidationOptions = {}
  ): { isValid: boolean; errors: string[] } {
    return validateCdmoApplicationData(data, options);
  },

  // Validate contact message
  validateContactMessage(data: Partial<ContactMessage>): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];

    // Validate name
    const nameValidation = validateName(data.name || '');
    if (!nameValidation.isValid) {
      errors.push(nameValidation.error!);
    }

    // Validate email
    const emailValidation = validateEmail(data.email || '');
    if (!emailValidation.isValid) {
      errors.push(emailValidation.error!);
    }

    // Validate company (optional)
    if (data.company) {
      const companyValidation = validateCompany(data.company);
      if (!companyValidation.isValid) {
        errors.push(companyValidation.error!);
      }
    }

    // Validate reason
    if (!data.reason || data.reason.trim().length === 0) {
      errors.push('Please select a reason for contact');
    }

    // Validate message
    const messageValidation = validateTextArea(data.message || '', 'Message', 10, 2000);
    if (!messageValidation.isValid) {
      errors.push(messageValidation.error!);
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }
};

// Helper to sanitize search terms for safe PostgREST .ilike() filter usage.
// Allowlist only alphanumerics, spaces, @, ., and - to prevent injection of structural
// PostgREST .or() delimiters such as ), (, and . into the filter string.
function sanitizeSearchTerm(rawSearch: string): string {
  return rawSearch.trim().replace(/[^a-zA-Z0-9 @.-]/g, '');
}

// Database service functions with enhanced validation
export const dbService = {
  // Startup signup functions
  async createStartupSignup(data: StartupSignup) {
    // Backend validation
    const validation = validationService.validateUserSignup(data);
    if (!validation.isValid) {
      throw new Error(`Validation failed: ${validation.errors.join(', ')}`);
    }

    const { error } = await supabase
      .from('startup_signups')
      .insert([{
        name: data.name,
        email: data.email,
        company: data.company,
        role: data.role,
        development_stage:
          data.role === 'startup'
            ? (data.development_stage ?? [])
            : null,

        looking_for:
          data.role === 'startup'
            ? data.looking_for
            : [],

        challenge:
          data?.role === 'startup'
            ? data?.challenge || null
            : null

      }]);

    if (error) {
      console.error('Error creating startup signup:', error);
      throw error;
    }
  },

  // User profile functions
  async createUserProfile(data: User) {
    // Backend validation
    const validation = validationService.validateUserSignup(data);
    if (!validation.isValid) {
      throw new Error(`Validation failed: ${validation.errors.join(', ')}`);
    }

    const { error } = await supabase
      .from('users')
      .insert([{
        id: data.id,
        name: data.name,
        email: data.email,
        username: data.username,
        company: data.company,
        role: data.role,
        job_role: data.job_role,
        development_stage: data.development_stage ?? [],
        looking_for: data.looking_for,
        challenge: data?.challenge || null,
        website: data?.website || null
      }]);

    if (error) {
      console.error('Error creating user profile:', error);
      throw error;
    }
  },

  // CDMO application functions
  async createCDMOApplication(
    data: CDMOApplication,
    options: { validationMode?: CdmoApplicationValidationMode } = {}
  ) {
    // Backend validation
    const validation = validationService.validateCDMOApplication(data, { mode: options.validationMode });
    if (!validation.isValid) {
      throw new Error(`Validation failed: ${validation.errors.join(', ')}`);
    }

    const isUnclaimedAdminProfile = !!data?.is_admin_created && !data?.user_id;
    const claimToken = isUnclaimedAdminProfile ? crypto.randomUUID() : null;
    const adminClaimToken = isUnclaimedAdminProfile ? crypto.randomUUID() : null;

    const claimExpiresAt = claimToken ? (() => {
      const d = new Date();
      d.setDate(d.getDate() + 14);
      return d.toISOString();
    })() : null;

    const { data: insertedData, error } = await supabase
      .from('cdmo_applications')
      .insert([buildCdmoApplicationRow(data, { claimToken, adminClaimToken, claimTokenExpiresAt: claimExpiresAt })])
      .select()
      .single();


    if (error) {
      console.error('Error creating CDMO application:', error);
      throw error;
    }

    return insertedData;
  },

  /**
   * Profiles whose website could be on one of `domains`, for the admin CSV bulk upload's duplicate
   * check; `createExistingDomainLookup` keeps the exact matches. The current website is searched, not
   * the stored `normalized_domain`, which can be empty or out of date (see `findWebsiteCandidates`).
   * Pages are ordered by id, so paging past the server's row cap misses no row.
   *
   * @param domains - Domains as `normalizeWebsiteDomain` produces them
   * @returns Each candidate profile once, with its website
   */
  async findCdmoWebsiteCandidates(domains: string[]): Promise<CdmoWebsiteRecord[]> {
    return findWebsiteCandidates(domains, async (filter, from, to) => {
      let query = supabase
        .from('cdmo_applications')
        .select('id, company_name, website', { count: 'exact' })
        .not('website', 'is', null);
      if (filter) query = query.or(filter);
      const { data, error, count } = await query.order('id').range(from, to);
      if (error) {
        console.error('Error looking up CDMO websites:', error);
        throw error;
      }
      const rows: CdmoWebsiteRecord[] = data ?? [];
      return { rows, total: count };
    });
  },

  /**
   * Existing profiles holding any of `feiNumbers` — the bulk upload's version of the form's
   * `checkFeiNumberExists`. `fei_number` stores a comma-separated list, so candidates are found with
   * a substring match and then kept only when one of their FEIs matches exactly.
   *
   * @param feiNumbers - FEI numbers (digits only; anything else is ignored)
   * @returns Matching profiles with their stored `fei_number`
   */
  async findCdmosByFeiNumbers(
    feiNumbers: string[]
  ): Promise<Array<{ id: string; company_name: string; fei_number: string | null }>> {
    const wanted = [...new Set(feiNumbers.map((fei) => fei.trim()).filter((fei) => /^\d{7,10}$/.test(fei)))];
    if (wanted.length === 0) return [];

    const matches = new Map<string, { id: string; company_name: string; fei_number: string | null }>();
    const CHUNK_SIZE = 40;
    for (let i = 0; i < wanted.length; i += CHUNK_SIZE) {
      const chunk = wanted.slice(i, i + CHUNK_SIZE);
      const { data, error } = await supabase
        .from('cdmo_applications')
        .select('id, company_name, fei_number')
        .or(chunk.map((fei) => `fei_number.ilike.*${fei}*`).join(','));
      if (error) {
        console.error('Error looking up CDMOs by FEI number:', error);
        throw error;
      }
      for (const row of (data || []) as Array<{ id: string; company_name: string; fei_number: string | null }>) {
        const stored = (row.fei_number ?? '').split(',').map((fei) => fei.trim());
        if (stored.some((fei) => wanted.includes(fei))) matches.set(row.id, row);
      }
    }
    return [...matches.values()];
  },

  /**
   * Records bulk-upload outcomes in `cdmo_import_rows` (admins only, enforced by RLS). Written in
   * chunks so a 500-row upload is a handful of requests.
   */
  async insertCdmoImportRows(rows: CdmoImportAuditRow[]): Promise<void> {
    const CHUNK_SIZE = 100;
    for (let i = 0; i < rows.length; i += CHUNK_SIZE) {
      const { error } = await supabase.from('cdmo_import_rows').insert(rows.slice(i, i + CHUNK_SIZE));
      if (error) {
        console.error('Error writing CDMO import audit rows:', error);
        throw error;
      }
    }
  },

  // Claim an unclaimed CDMO application
  async claimCDMOApplication(userId: string, email: string, token?: string, isTransfer: boolean = false) {
    const normalizedEmail = email.toLowerCase().trim();

    // If caller indicates this is an ownership transfer token, delegate to the transfer flow
    if (token && isTransfer) {
      // Use completeOwnershipTransfer which will validate the token and perform the claim atomically
      const result = await (this as any).completeOwnershipTransfer(token, userId, normalizedEmail);
      return result?.cdmoId ?? null;
    }

    // Pre-check for email collision with claimed profiles (as per AC-1)
    const { data: collision } = await supabase
      .from('cdmo_applications')
      .select('id')
      .eq('email', normalizedEmail)
      .not('user_id', 'is', null)
      .maybeSingle();

    if (collision) {
      throw new Error(VALIDATION_MESSAGES.EMAIL_ALREADY_REGISTERED_TO_CDMO);
    }

    let query = supabase
      .from('cdmo_applications')
      .select('*, claim_token_expires_at, admin_claim_token')
      .is('user_id', null);

    if (token) {
      // For regular tokens, check both claim_token and admin_claim_token
      query = query.or(`claim_token.eq.${token},admin_claim_token.eq.${token}`);
    } else {
      // Fallback to email matching if no token provided
      // Requirement: ownership is based on 'email' column only
      query = query.eq('email', normalizedEmail);
    }

    const { data: unclaimedApp, error: fetchError } = await query.maybeSingle();

    if (fetchError) {
      console.error('Error finding unclaimed CDMO application:', fetchError);
      throw fetchError;
    }

    if (!unclaimedApp) {
      return null;
    }

    // Validate token expiry and email for transfers
    // Validate regular claim token (transfer tokens are handled earlier)
    if (token) {
      const isAdminToken = unclaimedApp?.admin_claim_token === token;

      // Ensure token matches either claim_token or admin_claim_token
      if (
        unclaimedApp.claim_token !== token &&
        unclaimedApp.admin_claim_token !== token
      ) {
        throw new Error(VALIDATION_MESSAGES.TRANSFER_LINK_INVALID);
      }

      // EXCLUSIVE EMAIL RESTRICTION:
      // Requirement: Ownership is based on 'email' column only
      const allowedEmails = [
        unclaimedApp.email?.toLowerCase().trim()
      ].filter(Boolean);

      if (allowedEmails.length > 0 && !allowedEmails.includes(normalizedEmail)) {
        throw new Error(VALIDATION_MESSAGES.CLAIM_RESTRICTED_TO_EMAIL(allowedEmails[0]));
      }

      // Admin tokens never expire
      if (!isAdminToken) {
        if (unclaimedApp?.claim_token_expires_at) {
          const expiryDate = new Date(unclaimedApp.claim_token_expires_at);
          if (expiryDate < new Date()) {
            throw new Error(VALIDATION_MESSAGES.CLAIM_TOKEN_EXPIRED);
          }
        }
      }
    }


    // 2. Link the user to the application and mark as claimed
    // Ensure the application's email fields reflect the claiming address (whether primary or secondary)
    const normalizedClaimEmail = email?.toLowerCase().trim();

    // Clean up secondary emails: remove the claimer's email if it was there
    const existingSecondary: string[] = (unclaimedApp.secondary_emails || [])
      .map((e: string) => e?.toLowerCase().trim())
      .filter(Boolean);
    const newSecondary = existingSecondary.filter(e => e !== normalizedClaimEmail);

    // If the original primary email was different and not the claimer's,
    // we should probably keep it as a secondary email so we don't lose that contact?
    // Or just replace primary. The request says "email field will have the email which is entered in sign up".
    // So both public 'email' and internal 'primary_email' should be the claimer's.
    // If there was an old primary email that IS NOT the claimer's, let's move it to secondary to be safe.
    const oldPrimary = unclaimedApp.primary_email?.toLowerCase().trim();
    if (oldPrimary && oldPrimary !== normalizedClaimEmail && !newSecondary.includes(oldPrimary)) {
      newSecondary.push(oldPrimary);
    }

    const updatePayload: any = {
      user_id: userId,
      status: CDMO_STATUS.CLAIMED,
      claim_token: null, // Clear token after claim
      admin_claim_token: null, // Clear admin token after claim
      // Primary email is now the verified user's email
      primary_email: normalizedClaimEmail,
      email: normalizedClaimEmail,
      secondary_emails: newSecondary,
      campaign_active: false,
      // Keep claim_email_stage + claim_campaign_started_at for Campaign History metrics.
    };

    // Atomic claim: re-assert user_id IS NULL in the WHERE clause (not just at read
    // time above) so a concurrent claim of the same row cannot silently "succeed"
    // here with zero rows actually updated. `.select().single()` forces a distinguishable
    // error when the row no longer matches, instead of a false-positive empty success.
    //
    // Deliberately NOT also filtering status = 'unclaimed' here: admin-reserved
    // profiles are saved with status = 'claimed' and user_id = NULL (see
    // createCDMOApplication's statusToSave, and CDMOProfile.tsx's status: formData.status)
    // pending the real owner claiming them via token. user_id IS NULL alone is the
    // correct "not yet owned" invariant — it still closes the race, since the first
    // successful claim sets user_id, making a concurrent second write match zero rows.
    const { error: updateError } = await supabase
      .from('cdmo_applications')
      .update(updatePayload)
      .eq('id', unclaimedApp.id)
      .is('user_id', null)
      .select('id')
      .single();

    if (updateError) {
      if (updateError.code === 'PGRST116') {
        // No rows matched: another claim won the race between our read and this write.
        throw new Error(VALIDATION_MESSAGES.ACCOUNT_CLAIMED_BY_ANOTHER);
      }
      console.error('Error claiming CDMO application:', updateError);
      throw updateError;
    }

    // Step 3: Ensure user's role is set to 'cdmo'
    const { error: roleError } = await supabase
      .from('users')
      .update({ role: 'cdmo' })
      .eq('id', userId);

    if (roleError) {
      console.warn('Error updating user role during claim:', roleError);
    }

    // 3. Record the claim in cdmo_claims table (using upsert to avoid duplicates with trigger)
    await supabase
      .from('cdmo_claims')
      .upsert({
        cdmo_id: unclaimedApp.id,
        user_id: userId,
        status: 'verified',
        verified_at: new Date().toISOString()
      }, { onConflict: 'cdmo_id,user_id' });

    // Send admin notification asynchronously
    void supabase.functions.invoke('send-claim-success-admin', {
      body: { cdmoId: unclaimedApp.id, userId, claimantEmail: normalizedClaimEmail }
    }).catch(err => console.error(VALIDATION_MESSAGES.FAILED_TO_TRIGGER_ADMIN_CLAIM_EMAIL, err));

    return unclaimedApp.id;
  },

  // Force-claim an application by id (used as a fallback when regular claim query misses)
  async forceClaimApplicationById(appId: string, userId: string, claimEmail: string) {
    try {
      const normalizedClaimEmail = claimEmail?.toLowerCase().trim();

      // Fetch existing application to compute secondary emails
      const { data: app } = await supabase
        .from('cdmo_applications')
        .select('*')
        .eq('id', appId)
        .maybeSingle();

      if (!app) return null;

      const existingSecondary: string[] = (app.secondary_emails || [])
        .map((e: string) => e?.toLowerCase().trim())
        .filter(Boolean);
      const newSecondary = existingSecondary.filter(e => e !== normalizedClaimEmail);

      const oldPrimary = app.primary_email?.toLowerCase().trim();
      if (oldPrimary && oldPrimary !== normalizedClaimEmail && !newSecondary.includes(oldPrimary)) {
        newSecondary.push(oldPrimary);
      }

      const updatePayload: any = {
        user_id: userId,
        status: CDMO_STATUS.CLAIMED,
        claim_token: null,
        // Only set public email to the claimer's address. Keep primary_email unchanged.
        email: normalizedClaimEmail,
        secondary_emails: newSecondary,
        campaign_active: false,
        // Keep claim_email_stage + claim_campaign_started_at for Campaign History metrics.
      };

      const { error } = await supabase
        .from('cdmo_applications')
        .update(updatePayload)
        .eq('id', appId);

      if (error) {
        console.error('Error force-claiming CDMO application:', error);
        throw error;
      }

      // Record the claim in cdmo_claims table (upsert to avoid duplicates)
      await supabase
        .from('cdmo_claims')
        .upsert({
          cdmo_id: appId,
          user_id: userId,
          status: 'verified',
          verified_at: new Date().toISOString()
        }, { onConflict: 'cdmo_id,user_id' });

      // Send admin notification asynchronously
      void supabase.functions.invoke('send-claim-success-admin', {
        body: { cdmoId: appId, userId, claimantEmail: normalizedClaimEmail }
      }).catch(err => console.error(VALIDATION_MESSAGES.FAILED_TO_TRIGGER_ADMIN_CLAIM_EMAIL, err));

      return appId;
    } catch (error) {
      console.error('Error in forceClaimApplicationById:', error);
      return null;
    }
  },

  // Fetch admin-created CDMO application by matching an email
  async getAdminCreatedData(email: string): Promise<CDMOApplication | null> {
    try {
      const normalized = email?.toLowerCase().trim();

      const { data, error } = await supabase
        .from('cdmo_applications')
        .select('*')
        .eq('is_admin_created', true)
        .eq('email', normalized)
        .limit(1)
        .maybeSingle();

      if (error) {
        if (error.code === 'PGRST116') return null;
        throw error;
      }

      return data ?? null;

    } catch (error) {
      console.error('Error in getAdminCreatedData:', error);
      return null;
    }
  },

  async getCDMOApplicationByToken(token: string): Promise<CDMOApplication | null> {
    const { data, error } = await supabase
      .from('cdmo_applications')
      .select('*')
      .or(`claim_token.eq.${token},admin_claim_token.eq.${token}`)
      .maybeSingle();
    if (error) {
      console.error('Error fetching CDMO application by token:', error);
      throw error;
    }

    return data;
  },

  // Check if CDMO profile is complete
  async checkCDMOProfileCompletion(userId: string): Promise<{ isComplete: boolean; hasProfile: boolean }> {
    try {
      const { data, error } = await supabase
        .from('cdmo_applications')
        .select('company_name, contact_name, email, phone, primary_capabilities, typical_batch_sizes')
        .eq('user_id', userId)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          // No profile found
          return { isComplete: false, hasProfile: false };
        }
        throw error;
      }

      if (!data) {
        return { isComplete: false, hasProfile: false };
      }

      // Check if required fields are complete
      const isComplete = !!(
        data.company_name &&
        data.contact_name &&
        data.email &&
        data.phone &&
        data.primary_capabilities &&
        data.primary_capabilities.length > 0 &&
        data.typical_batch_sizes &&
        data.typical_batch_sizes.length > 0
      );

      return { isComplete, hasProfile: true };
    } catch (error) {
      console.error('Error checking CDMO profile completion:', error);
      return { isComplete: false, hasProfile: false };
    }
  },

  // Update CDMO application for a user
  async updateCDMOApplication(userId: string, data: Partial<CDMOApplication>) {
    // Backend validation (reuse validationService)
    const validation = validationService.validateCDMOApplication(data);
    if (!validation.isValid) {
      throw new Error(`Validation failed: ${validation.errors.join(', ')}`);
    }
    // Extract and normalize domain if website is provided in update
    const updateData: any = { ...data };
    if (data.website) {
      try {
        const url = /^https?:/i.test(data.website) ? data.website : `https://${data.website}`;
        const parsed = new URL(url);
        updateData.normalized_domain = parsed.hostname.toLowerCase().replace(/^www\./, '');
      } catch (e) {
        console.warn('Failed to normalize domain in update:', e);
      }
    }

    const { error } = await supabase
      .from('cdmo_applications')
      .update(updateData)
      .eq('user_id', userId);
    if (error) {
      console.error('Error updating CDMO application:', error);
      throw error;
    }
  },

  // Contact message functions
  async createContactMessage(data: ContactMessage) {
    // Backend validation
    const validation = validationService.validateContactMessage(data);
    if (!validation.isValid) {
      throw new Error(`Validation failed: ${validation.errors.join(', ')}`);
    }

    const { error } = await supabase
      .from('contact_messages')
      .insert([{
        name: data.name,
        email: data.email,
        company: data.company || null,
        reason: data.reason,
        message: data.message
      }]);

    if (error) {
      console.error('Error creating contact message:', error);
      throw error;
    }
  },
  /**
  * Fetches all synonym groups from the database.
  */
  async getSynonymGroups() {
    const { data, error } = await supabase
      .from('synonym_groups')
      .select('canonical_term, synonyms');

    if (error) {
      console.error('Error fetching synonyms:', error);
      return [];
    }
    return data;
  },

  // Fetch all CDMO applications
  async getAllCDMOApplications() {
    const { data, error } = await supabase
      .from('cdmo_applications')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching CDMO applications:', error);
      throw error;
    }

    return data || [];
  },

  /**
   * Paginated list of CDMOs eligible for the claim-email campaign (unclaimed, no owner, has claim token).
   * PostgREST combines multiple `.or()` filters with AND, so token eligibility and search can both apply.
   *
   * @param options.page - 1-based page index
   * @param options.pageSize - Max rows per page (capped at 200)
   * @param options.search - Optional case-insensitive match on company_name, email, or primary_email
   * @param options.campaignStatus - Optional filter: active campaign, idle (no active campaign), or all
   * @returns Matching rows for the page and total row count for the current filters
   */
  async getClaimCampaignEligibleCdmosPaginated(options: {
    page: number;
    pageSize: number;
    search?: string;
    campaignStatus?: 'all' | 'active' | 'idle';
  }): Promise<{ data: CDMOApplication[]; totalCount: number }> {
    const pageSize = Math.min(200, Math.max(1, options.pageSize));
    const page = Math.max(1, options.page);
    const from = (page - 1) * pageSize;
    const to = from + pageSize - 1;

    const rawSearch = (options.search ?? '').trim();
    const safeSearch = sanitizeSearchTerm(rawSearch);

    let query = supabase
      .from('cdmo_applications')
      .select('*', { count: 'exact' })
      .eq('status', CDMO_STATUS.UNCLAIMED)
      .is('user_id', null)
      .or('claim_token.not.is.null,admin_claim_token.not.is.null')
      .order('company_name', { ascending: true, nullsFirst: false });

    const status = options.campaignStatus ?? 'all';
    if (status === 'active') {
      query = query.eq('campaign_active', true);
    } else if (status === 'idle') {
      query = query.or('campaign_active.is.null,campaign_active.eq.false');
    }

    if (safeSearch.length > 0) {
      const pattern = `%${safeSearch}%`;
      query = query.or(`company_name.ilike.${pattern},email.ilike.${pattern},primary_email.ilike.${pattern}`);
    }

    const { data, error, count } = await query.range(from, to);

    if (error) {
      console.error('Error fetching claim campaign CDMO page:', error);
      throw error;
    }

    return { data: (data || []) as CDMOApplication[], totalCount: count ?? 0 };
  },

  /**
   * All profile IDs matching campaign-table filters that can still be started (no active campaign).
   * Used for “Select unclaimed” across paginated pages. Batched to respect PostgREST row limits.
   *
   * @param options.search - Same semantics as {@link getClaimCampaignEligibleCdmosPaginated} (debounced value from parent).
   * @param options.campaignStatus - When `active`, returns [] (nothing to start).
   * @returns Distinct string IDs in stable name order
   */
  async getClaimCampaignSelectableCdmoIds(options: {
    search?: string;
    campaignStatus?: 'all' | 'active' | 'idle';
  }): Promise<string[]> {
    const status = options.campaignStatus ?? 'all';
    if (status === 'active') {
      return [];
    }

    const rawSearch = (options.search ?? '').trim();
    const safeSearch = sanitizeSearchTerm(rawSearch);

    const BATCH = 1000;
    const ids: string[] = [];

    const buildQuery = () => {
      let q = supabase
        .from('cdmo_applications')
        .select('id')
        .eq('status', CDMO_STATUS.UNCLAIMED)
        .is('user_id', null)
        .or('claim_token.not.is.null,admin_claim_token.not.is.null')
        .or('campaign_active.is.null,campaign_active.eq.false')
        .order('company_name', { ascending: true, nullsFirst: false });

      if (safeSearch.length > 0) {
        const pattern = `%${safeSearch}%`;
        q = q.or(`company_name.ilike.${pattern},email.ilike.${pattern},primary_email.ilike.${pattern}`);
      }
      return q;
    };

    for (let from = 0; ; from += BATCH) {
      const { data, error } = await buildQuery().range(from, from + BATCH - 1);
      if (error) {
        console.error('Error fetching claim campaign selectable IDs:', error);
        throw error;
      }
      if (!data?.length) break;
      for (const row of data) {
        if (row?.id) ids.push(row.id as string);
      }
      if (data.length < BATCH) break;
    }

    return ids;
  },

  /**
   * Starts the controlled claim-email campaign for selected unclaimed CDMOs (admin-only Edge Function).
   * @param options.cdmoIds - Explicit profile IDs (ignored when allUnclaimed is true).
   * @param options.allUnclaimed - When true, server selects every eligible unclaimed CDMO with a claim token.
   * @param options.origin - Used for claim links in emails (defaults to `window.location.origin` in browser).
   */
  async startClaimEmailCampaign(options: {
    cdmoIds?: string[];
    allUnclaimed?: boolean;
    origin?: string;
  }): Promise<{
    results: { cdmoId: string; ok: boolean; detail?: string }[];
    count: number;
  }> {
    const origin =
      options.origin ??
      (typeof window !== 'undefined' ? window.location.origin : '') ??
      '';

    const { data, error } = await supabase.functions.invoke('start-claim-email-campaign', {
      body: {
        cdmoIds: options.allUnclaimed ? undefined : options.cdmoIds,
        allUnclaimed: !!options.allUnclaimed,
        origin,
      },
    });

    if (error) {
      console.error('startClaimEmailCampaign invoke error:', error);
      throw error;
    }

    return data as {
      results: { cdmoId: string; ok: boolean; detail?: string }[];
      count: number;
    };
  },

  async getSponsorInviteEmailTemplates() {
    const { data, error } = await supabase
      .from('sponsor_invite_email_templates')
      .select('step, subject, body, updated_at')
      .order('step', { ascending: true });

    if (error) {
      console.error('getSponsorInviteEmailTemplates:', error);
      throw error;
    }
    return data ?? [];
  },

  async upsertSponsorInviteEmailTemplate(template: {
    step: number;
    subject: string;
    body: string;
  }) {
    const { error } = await supabase.from('sponsor_invite_email_templates').upsert({
      step: template.step,
      subject: template.subject,
      body: template.body,
      updated_at: new Date().toISOString(),
    });

    if (error) {
      console.error('upsertSponsorInviteEmailTemplate:', error);
      throw error;
    }
  },

  async startSponsorInviteCampaign(options: {
    /** Per-recipient name + email (preferred; from the campaign form). */
    recipients?: { firstName?: string; email: string }[];
    emails?: string[];
    emailsText?: string;
    /** Base URL for invite links (defaults to `window.location.origin`). */
    origin?: string;
  }): Promise<{
    campaignId: string;
    results: { email: string; ok: boolean; detail?: string }[];
    count: number;
    started: number;
  }> {
    const origin =
      options.origin ??
      (typeof window !== 'undefined' ? window.location.origin : '') ??
      '';

    const { data, error } = await supabase.functions.invoke('start-sponsor-invite-campaign', {
      body: {
        recipients: options.recipients,
        emails: options.emails,
        emailsText: options.emailsText,
        origin,
      },
    });

    if (error) {
      console.error('startSponsorInviteCampaign:', error);
      throw error;
    }

    return data as {
      campaignId: string;
      results: { email: string; ok: boolean; detail?: string }[];
      count: number;
      started: number;
    };
  },

  async sendSponsorInviteTestEmail(options: {
    step: number;
    testEmail: string;
    origin?: string;
    subject?: string;
    body?: string;
  }) {
    const origin =
      options.origin ??
      (typeof window !== 'undefined' ? window.location.origin : '') ??
      '';

    const { data, error } = await supabase.functions.invoke('send-sponsor-invite-test-email', {
      body: { ...options, origin },
    });

    if (error) {
      console.error('sendSponsorInviteTestEmail:', error);
      throw error;
    }
    return data;
  },

  async getSponsorInviteCampaigns(limit = 50) {
    const { data, error } = await supabase
      .from('sponsor_invite_campaigns')
      .select('id, started_at, status, recipient_count, created_by, email_provider, external_campaign_id, provider_error')
      .order('started_at', { ascending: false })
      .limit(limit);

    if (error) {
      console.error('getSponsorInviteCampaigns:', error);
      throw error;
    }
    return data ?? [];
  },

  async getSponsorInviteRecipientsForCampaign(campaignId: string) {
    const { data, error } = await supabase
      .from('sponsor_invite_recipients')
      .select(
        'id, invited_email, invited_first_name, email_stage, sequence_active, email1_sent_at, email2_sent_at, email3_sent_at, greeting_path, link_clicked_at, converted_user_id, signup_email, unsubscribed_at, sequence_stopped_reason, created_at',
      )
      .eq('campaign_id', campaignId)
      .order('created_at', { ascending: true });

    if (error) {
      console.error('getSponsorInviteRecipientsForCampaign:', error);
      throw error;
    }
    return data ?? [];
  },

  async getSponsorInviteByToken(token: string) {
    const { data, error } = await supabase.rpc('get_sponsor_invite_by_token', {
      p_token: token,
    });

    if (error) {
      console.error('getSponsorInviteByToken:', error);
      throw error;
    }
    const row = Array.isArray(data) ? data[0] : data;
    return row as
      | {
          id: string;
          invited_email: string;
          converted_user_id: string | null;
          sequence_active: boolean;
        }
      | undefined;
  },

  async recordSponsorInviteLinkClick(token: string) {
    const { error } = await supabase.rpc('record_sponsor_invite_link_click', {
      p_token: token,
    });
    if (error) {
      console.error('recordSponsorInviteLinkClick:', error);
      throw error;
    }
  },

  async completeSponsorInviteConversion(token: string, userId: string, signupEmail: string) {
    const { error } = await supabase.rpc('complete_sponsor_invite_conversion', {
      p_token: token,
      p_user_id: userId,
      p_signup_email: signupEmail,
    });
    if (error) {
      console.error('completeSponsorInviteConversion:', error);
      throw error;
    }
  },

  async recordCdmoClaimLinkClick(cdmoId: string) {
    const { error } = await supabase.rpc('record_cdmo_claim_link_click', {
      p_cdmo_id: cdmoId,
    });
    if (error) {
      console.error('recordCdmoClaimLinkClick:', error);
      throw error;
    }
  },

  /** Campaign history for admin History tab (paginated; no 100-row cap). */
  async getCampaignHistoryRows(options?: {
    page?: number;
    pageSize?: number;
    typeFilter?: CampaignHistoryTypeFilter;
  }) {
    const pageSize = options?.pageSize ?? CAMPAIGN_HISTORY_DEFAULT_PAGE_SIZE;
    const typeFilter = options?.typeFilter ?? 'all';
    const requestedPage = options?.page ?? 1;

    const [
      sponsorHeadersRes,
      cdmoStartsRes,
      cdmoClaimedFromCampaign,
      sponsorConversionsCount,
    ] = await Promise.all([
      supabase
        .from('sponsor_invite_campaigns')
        .select('id, started_at')
        .order('started_at', { ascending: false }),
      supabase
        .from('cdmo_applications')
        .select('claim_campaign_started_at')
        .not('claim_campaign_started_at', 'is', null)
        .order('claim_campaign_started_at', { ascending: false }),
      supabase
        .from('cdmo_applications')
        .select('id', { count: 'exact', head: true })
        .not('claim_campaign_started_at', 'is', null)
        .not('user_id', 'is', null),
      supabase
        .from('sponsor_invite_recipients')
        .select('id', { count: 'exact', head: true })
        .not('converted_user_id', 'is', null),
    ]);

    if (sponsorHeadersRes.error) {
      console.error('getCampaignHistoryRows sponsor campaigns:', sponsorHeadersRes.error);
      throw sponsorHeadersRes.error;
    }
    if (cdmoStartsRes.error) {
      console.error('getCampaignHistoryRows cdmo:', cdmoStartsRes.error);
      throw cdmoStartsRes.error;
    }

    const sponsorHeaders = sponsorHeadersRes.data ?? [];
    const cdmoBatchStarts = collectDistinctCdmoBatchStarts(cdmoStartsRes.data ?? []);
    const sponsorCampaignCount = sponsorHeaders.length;
    const cdmoCampaignCount = cdmoBatchStarts.length;

    const fullIndex = buildCampaignHistoryIndex(sponsorHeaders, cdmoBatchStarts);
    const filteredIndex = filterCampaignHistoryIndex(fullIndex, typeFilter);
    const { slice: pageIndex, totalCount, totalPages, page } = paginateCampaignHistoryIndex(
      filteredIndex,
      requestedPage,
      pageSize,
    );

    const pageRows = await this.buildCampaignHistoryRowsForIndex(pageIndex);

    return {
      rows: pageRows,
      page,
      pageSize,
      totalCount,
      totalPages,
      totalCdmoClaimedFromCampaign: cdmoClaimedFromCampaign.count ?? 0,
      totalSponsorSignUps: sponsorConversionsCount.count ?? 0,
      sponsorCampaignCount,
      cdmoCampaignCount,
    };
  },

  async loadUnsubscribedEmailsForCampaignHistory(emails: string[]): Promise<Set<string>> {
    const unsubscribedEmailSet = new Set<string>();
    const unique = [...new Set(emails.filter(Boolean))];
    if (unique.length === 0) return unsubscribedEmailSet;

    for (const chunk of chunkStrings(unique, CAMPAIGN_HISTORY_EMAIL_IN_CHUNK_SIZE)) {
      const { data: unsubRows, error: unsubErr } = await supabase
        .from('email_subscriptions')
        .select('email')
        .eq('unsubscribed', true)
        .in('email', chunk);
      if (unsubErr) {
        console.error('getCampaignHistoryRows email_subscriptions:', unsubErr);
        continue;
      }
      for (const row of unsubRows ?? []) {
        if (row.email) unsubscribedEmailSet.add(row.email.toLowerCase());
      }
    }
    return unsubscribedEmailSet;
  },

  async buildCampaignHistoryRowsForIndex(indexEntries: CampaignHistoryIndexEntry[]) {
    if (indexEntries.length === 0) return [];

    const sponsorIds = indexEntries
      .filter((e) => e.campaignType === 'sponsor_invite')
      .map((e) => e.id);
    const cdmoBatchKeys = indexEntries
      .filter((e) => e.campaignType === 'cdmo_claim')
      .map((e) => e.dateStarted);

    const sponsorCampaignDetails = await Promise.all(
      sponsorIds.map(async (id) => {
        const { data: campaign, error } = await supabase
          .from('sponsor_invite_campaigns')
          .select('id, started_at, status, recipient_count, created_by')
          .eq('id', id)
          .maybeSingle();
        if (error || !campaign) {
          return null;
        }
        const recipients = await this.getSponsorInviteRecipientsForCampaign(id);
        return { campaign, recipients };
      }),
    );

    type CdmoHistoryProfile = {
      id: string;
      company_name?: string | null;
      campaign_active?: boolean | null;
      claim_email_stage?: number | null;
      claim_campaign_started_at?: string | null;
      claim_link_clicked_at?: string | null;
      user_id?: string | null;
      email?: string | null;
      primary_email?: string | null;
    };

    let cdmoProfiles: CdmoHistoryProfile[] = [];
    if (cdmoBatchKeys.length > 0) {
      const { data, error } = await supabase
        .from('cdmo_applications')
        .select(
          'id, company_name, campaign_active, claim_email_stage, claim_campaign_started_at, claim_link_clicked_at, user_id, status, email, primary_email',
        )
        .in('claim_campaign_started_at', cdmoBatchKeys);
      if (error) {
        console.error('getCampaignHistoryRows cdmo profiles:', error);
        throw error;
      }
      cdmoProfiles = (data ?? []) as CdmoHistoryProfile[];
    }

    const sponsorDetails = sponsorCampaignDetails.filter(
      (d): d is NonNullable<typeof d> => d !== null,
    );

    const cdmoEmails = cdmoProfiles.map((row) => cdmoClaimRecipientEmail(row));
    const sponsorEmails = sponsorDetails.flatMap(({ recipients }) =>
      recipients
        .map((r) => r.invited_email?.trim().toLowerCase())
        .filter((e): e is string => Boolean(e)),
    );
    const unsubscribedEmailSet = await this.loadUnsubscribedEmailsForCampaignHistory([
      ...cdmoEmails,
      ...sponsorEmails,
    ]);

    const allConvertedUserIds = sponsorDetails.flatMap((d) =>
      d.recipients.filter((r) => r.converted_user_id).map((r) => r.converted_user_id),
    );

    let userMap: Record<string, { name: string; company: string; email: string }> = {};
    if (allConvertedUserIds.length > 0) {
      const { data: users } = await supabase
        .from('users')
        .select('id, name, company, email')
        .in('id', allConvertedUserIds);

      if (users) {
        userMap = users.reduce((acc, u) => {
          acc[u.id] = u;
          return acc;
        }, {} as Record<string, { name: string; company: string; email: string }>);
      }
    }

    const sponsorRowById = new Map(
      sponsorDetails.map(({ campaign: c, recipients }) => {
        const row = this.buildSponsorCampaignHistoryRow(c, recipients, unsubscribedEmailSet, userMap);
        return [c.id, row] as const;
      }),
    );

    const cdmoBatchMap = groupCdmoProfilesByCampaignStart(cdmoProfiles);
    const cdmoRowByStartedAt = new Map(
      [...cdmoBatchMap.entries()].map(([startedAt, profiles]) => {
        const row = this.buildCdmoCampaignHistoryBatchRow(
          startedAt,
          profiles,
          unsubscribedEmailSet,
        );
        return [startedAt, row] as const;
      }),
    );

    return indexEntries
      .map((entry) => {
        if (entry.campaignType === 'sponsor_invite') {
          return sponsorRowById.get(entry.id);
        }
        return cdmoRowByStartedAt.get(entry.dateStarted);
      })
      .filter((row): row is NonNullable<typeof row> => row != null);
  },

  buildSponsorCampaignHistoryRow(
    c: {
      id: string;
      started_at: string;
      status: string;
      recipient_count: number;
    },
    recipients: {
      invited_email: string;
      email_stage?: number | null;
      link_clicked_at?: string | null;
      converted_user_id?: string | null;
      signup_email?: string | null;
      unsubscribed_at?: string | null;
    }[],
    unsubscribedEmailSet: Set<string>,
    userMap: Record<string, { name: string; company: string; email: string }>,
  ) {
    const metrics = buildSponsorHistoryMetrics(recipients);
    const unsubscribedCount = recipients.filter((r) => {
      const invited = r.invited_email?.trim().toLowerCase();
      return Boolean(r.unsubscribed_at || (invited && unsubscribedEmailSet.has(invited)));
    }).length;
    const conversions = recipients
      .filter((r) => r.converted_user_id)
      .map((r) => {
        const u = userMap[r.converted_user_id!];
        const accountEmail = u?.email || r.signup_email || r.invited_email;
        return {
          name: u?.name || r.signup_email || r.invited_email,
          company: u?.company,
          email: accountEmail,
          invitedEmail:
            r.signup_email && r.invited_email && r.signup_email !== r.invited_email
              ? r.invited_email
              : undefined,
        };
      });

    const detailLabel =
      recipients.length === 1 && recipients[0]?.invited_email
        ? recipients[0].invited_email
        : `${recipients.length || c.recipient_count} biotech / sponsor emails`;

    return {
      campaignType: 'sponsor_invite' as const,
      id: c.id,
      dateStarted: c.started_at,
      recipientCount: c.recipient_count,
      currentStage: maxEmailStage(recipients),
      ...metrics,
      unsubscribed: unsubscribedCount,
      signUps: metrics.signUps,
      conversions,
      status: deriveSponsorCampaignStatus({
        campaignStatus: c.status,
        recipients: recipients.map((r) => {
          const invited = r.invited_email?.trim().toLowerCase();
          return {
            ...r,
            unsubscribed_at:
              r.unsubscribed_at ||
              (invited && unsubscribedEmailSet.has(invited) ? new Date().toISOString() : null),
          };
        }),
      }),
      detailLabel,
      recipients: recipients.map((r) => {
        const invited = r.invited_email?.trim().toLowerCase();
        const isUnsub =
          Boolean(r.unsubscribed_at) || Boolean(invited && unsubscribedEmailSet.has(invited));
        return {
          email: r.invited_email,
          signupEmail: r.signup_email,
          status: isUnsub
            ? 'unsubscribed'
            : r.converted_user_id
              ? 'converted'
              : r.link_clicked_at
                ? 'clicked'
                : 'sent',
          stage: r.email_stage,
        };
      }),
    };
  },

  buildCdmoCampaignHistoryBatchRow(
    startedAt: string,
    profiles: {
      company_name?: string | null;
      campaign_active?: boolean | null;
      claim_email_stage?: number | null;
      claim_link_clicked_at?: string | null;
      user_id?: string | null;
      email?: string | null;
      primary_email?: string | null;
    }[],
    unsubscribedEmailSet: Set<string>,
  ) {
    const metricInputs = profiles.map((row) => {
      const contactEmail = cdmoClaimRecipientEmail(row);
      const isUnsubscribed = contactEmail ? unsubscribedEmailSet.has(contactEmail) : false;
      return {
        row,
        contactEmail,
        isUnsubscribed,
        metricsInput: {
          claim_email_stage: row.claim_email_stage,
          claim_link_clicked_at: row.claim_link_clicked_at,
          user_id: row.user_id,
          unsubscribed: isUnsubscribed,
        },
      };
    });

    const metrics = buildCdmoBatchHistoryMetrics(metricInputs.map((m) => m.metricsInput));
    const anyUnsubscribed = metricInputs.some((m) => m.isUnsubscribed);
    const currentStage = profiles.reduce((max, row) => Math.max(max, row.claim_email_stage ?? 0), 0);
    const conversions = metricInputs
      .filter((m) => m.row.user_id)
      .map((m) => ({
        name: m.row.company_name,
        company: m.row.company_name,
        email: m.contactEmail,
      }));
    const recipients = metricInputs.map((m) => ({
      email: m.contactEmail || m.row.company_name || '—',
      companyName: m.row.company_name ?? undefined,
      status: m.isUnsubscribed
        ? 'unsubscribed'
        : m.row.user_id
          ? 'converted'
          : m.row.claim_link_clicked_at
            ? 'clicked'
            : 'sent',
      stage: m.row.claim_email_stage,
    }));

    const detailLabel =
      profiles.length === 1 && profiles[0]?.company_name
        ? profiles[0].company_name
        : `${profiles.length} CDMO profiles`;

    return {
      campaignType: 'cdmo_claim' as const,
      id: `batch-${startedAt}`,
      dateStarted: startedAt,
      recipientCount: profiles.length,
      currentStage,
      ...metrics,
      signUps: metrics.signUps,
      conversions,
      status: deriveCdmoBatchCampaignStatus(profiles, { anyUnsubscribed }),
      detailLabel,
      recipients,
    };
  },

  // Fetch all startup signups
  async getAllStartupSignups() {
    const { data, error } = await supabase
      .from('startup_signups')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching startup signups:', error);
      throw error;
    }

    return data || [];
  },

  // Fetch CDMO application by user ID
  async getCDMOApplicationByUserId(userId: string) {
    const { data, error } = await supabase
      .from('cdmo_applications')
      .select('*')
      .eq('user_id', userId)
      .single();

    if (error) {
      console.error('Error fetching CDMO application by user ID:', error);
      throw error;
    }

    return data;
  },

  // Introduction request functions
  async createIntroductionRequest(data: IntroductionRequest) {


    const { data: createdRequest, error } = await supabase
      .from('introduction_requests')
      .insert([{
        requester_user_id: data.requester_user_id,
        requester_name: data.requester_name,
        requester_email: data.requester_email,
        cdmo_id: data.cdmo_id,
        request_details: data.request_details,
        shared_field_settings: data.shared_field_settings || null,
        shared_project_brief: data.shared_project_brief || null,
        status: data.status || 'pending'
      }])
      .select(`
        id,
        requester_user_id,
        cdmo_id,
        status,
        created_at,
        shared_field_settings,
        shared_project_brief
      `)
      .single();

    if (error) {
      if (error.code === '23505') {
        // Duplicate active request for this (requester_user_id, cdmo_id) pair.
        // Per product decision, this is idempotent: return the existing request
        // instead of erroring, so a retry-after-failure or double-click can't
        // create a hidden duplicate (client-reported concern on issue #6).
        const { data: existing, error: fetchError } = await supabase
          .from('introduction_requests')
          .select(`
            id,
            requester_user_id,
            cdmo_id,
            status,
            created_at,
            shared_field_settings,
            shared_project_brief
          `)
          .eq('requester_user_id', data.requester_user_id)
          .eq('cdmo_id', data.cdmo_id)
          .neq('status', 'declined')
          .order('created_at', { ascending: false })
          .limit(1)
          .single();

        if (fetchError || !existing) {
          console.error('Error fetching existing introduction request after duplicate-key conflict:', fetchError);
          throw error;
        }

        return existing;
      }

      console.error('Error creating introduction request:', error);
      throw error;
    }


    // Fire-and-forget: don’t block the response on notifications
    void this.triggerCDMONotification({ ...data, id: createdRequest.id });

    return createdRequest;
  },

  // Trigger CDMO notification for new introduction request
  async triggerCDMONotification(requestData: IntroductionRequest) {
    try {
      // Parallelize independent reads
      const { data: cdmoApplication, error: cdmoError } = await supabase
        .from('cdmo_applications')
        .select('user_id, claim_token, claim_token_expires_at, is_admin_created, status')
        .eq('id', requestData.cdmo_id)
        .single();

      if (cdmoError) {
        console.error('Error fetching CDMO application/user:', cdmoError);
        return;
      }

      // If unclaimed, ensure token is fresh (14 days from new request)
      if (!cdmoApplication?.user_id) {
        const now = new Date();
        const expiry = cdmoApplication?.claim_token_expires_at ? new Date(cdmoApplication.claim_token_expires_at) : null;

        // Generate/Refresh token if missing or expiring soon (within 2 days)
        if (!cdmoApplication?.claim_token || !expiry || (expiry.getTime() - now.getTime()) < (2 * 24 * 60 * 60 * 1000)) {
          const newToken = crypto.randomUUID();
          const newExpiry = new Date();
          newExpiry.setDate(newExpiry.getDate() + 14);

          await supabase
            .from('cdmo_applications')
            .update({
              claim_token: newToken,
              claim_token_expires_at: newExpiry.toISOString(),
              reminder_cycle_start_at: now.toISOString()
            })
            .eq('id', requestData.cdmo_id);
        }
      }

      const sponsorDisplayName = getSponsorDisplayName(
        requestData.shared_field_settings,
        requestData.shared_project_brief
      );

      // If claimed or reserved, proceed with notification record and subscription check
      const isReservedClaimed = cdmoApplication?.is_admin_created && cdmoApplication?.status === CDMO_STATUS.CLAIMED;
      if (cdmoApplication?.user_id || isReservedClaimed) {
        // Stripe lookups (sequential due to dependency)
        let hasActiveSubscription = false;
        if (cdmoApplication?.user_id) {
          const { data: customerData } = await supabase
            .from('stripe_customers')
            .select('customer_id')
            .eq('user_id', cdmoApplication.user_id)
            .is('deleted_at', null)
            .single();

          if (customerData?.customer_id) {
            const { data: subscription } = await supabase
              .from('stripe_subscriptions')
              .select('*')
              .eq('customer_id', customerData.customer_id)
              .in('status', ['active', 'active_until_cancelation'])
              .single();
            hasActiveSubscription = !!subscription;
          }
        }

        const notificationTitle = 'New Introduction Request';
        const notificationMessage = hasActiveSubscription
          ? `You have received a new introduction request from ${sponsorDisplayName}.`
          : 'You have received a new introduction request! Upgrade to Premium to see full details.';

        const { error: notificationError } = await supabase.from('notifications').insert({
          user_id: cdmoApplication.user_id || null,
          cdmo_id: requestData.cdmo_id,
          introduction_request_id: requestData.id,
          type: 'introduction_request',
          title: notificationTitle,
          message: notificationMessage,
          is_read: false,
          email_sent: false,
        });

        if (notificationError) {
          console.error('Error creating notification record:', notificationError);
        }
      }

      // Optional: email/edge function call (non-blocking to UI)
      try {
        const payload: any = {
          cdmo_id: requestData.cdmo_id,
          introduction_request_id: requestData.id!,
          sponsor_display_name: sponsorDisplayName,
          shared_project_brief:
            requestData.shared_project_brief ||
            buildLegacySafeSharedBrief(requestData.request_details || {}),
          origin: window.location.origin, // Pass the current origin to the function
        };

        // If the profile is unclaimed, fetch all admins from the database (as per latest requirement)
        if (!cdmoApplication?.user_id) {
          // Goes through list_admin_emails() rather than a direct table read: the admin table's
          // SELECT policy is own-row-only, so a broad select here would return nothing.
          const { data: adminEmailRows } = await supabase.rpc('list_admin_emails');
          const adminEmails = (adminEmailRows as string[] | null)?.filter(Boolean) || [];
          if (adminEmails.length > 0) {
            payload.admin_notification = true;
            payload.admin_emails = adminEmails;
            payload.claimant_email = requestData.requester_email;
          }
        }

        // Determine which function to call based on the existence of a user_id or if it is a reserved claimed profile
        const isReservedClaimed = cdmoApplication?.is_admin_created && cdmoApplication?.status === CDMO_STATUS.CLAIMED;
        const functionName = (cdmoApplication?.user_id || isReservedClaimed)
          ? 'send-cdmo-notification'
          : 'send-unclaimed-cdmo-notification';

        const response = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/${functionName}`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
            },
            body: JSON.stringify(payload),
          }
        );
        if (!response.ok) {
          const errorData = await response.json();
          console.error(`Notification function (${functionName}) error:`, errorData);
        }
      } catch (fetchError) {
        console.error('Error calling notification function:', fetchError);
      }
    } catch (error) {
      console.error('Error triggering CDMO notification:', error);
    }
  },

  // Fetch introduction requests for a specific user with full CDMO data
  async getUserIntroductionRequests(userId: string) {
    const { data, error } = await supabase
      .from('introduction_requests')
      .select(`
        id,
        requester_user_id,
        cdmo_id,
        status,
        created_at,
        shared_field_settings,
        shared_project_brief,
        cdmo_applications!introduction_requests_cdmo_id_fkey (
          id,
          company_name,
          contact_name,
          email,
          phone,
          website,
          primary_capabilities,
          target_client_types,
          typical_batch_sizes,
          gmp_certifications,
          facility_locations_details,
          speed_to_start,
          engagement_scales,
          description
        )
      `)
      .eq('requester_user_id', userId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching user introduction requests:', error);
      throw error;
    }

    return data || [];
  },

  // Get full CDMO application by ID
  async getCDMOApplicationById(cdmoId: string) {
    const { data, error } = await supabase
      .from('cdmo_applications')
      .select('*')
      .eq('id', cdmoId)
      .single();

    if (error) {
      console.error('Error fetching CDMO application:', error);
      throw error;
    }

    return data;
  },

  // Fetch introduction requests for a specific CDMO
  async getCDMOIntroductionRequests(cdmoId: string): Promise<CdmoSafeIntroductionRequest[]> {
    const { data, error } = await supabase
      .from('introduction_requests')
      .select(`
        id,
        requester_user_id,
        cdmo_id,
        shared_field_settings,
        shared_project_brief,
        status,
        created_at
      `)
      .eq('cdmo_id', cdmoId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching CDMO introduction requests:', error);
      throw error;
    }

    return (data || []).map((request) =>
      toCdmoSafeIntroductionRequest(request as CdmoIntroductionRequestDbRow)
    );
  },

  // Update introduction request status
  async updateIntroductionRequestStatus(requestId: string, status: string) {
    const { error } = await supabase
      .from('introduction_requests')
      .update({ status })
      .eq('id', requestId);

    if (error) {
      console.error('Error updating introduction request status:', error);
      throw error;
    }

    // If status is accepted, trigger email notifications
    if (status === 'accepted') {
      await this.triggerAcceptanceEmails(requestId);
      await this.triggerReviewEmails(requestId);
    }
  },

  // Trigger review email notifications
  async triggerReviewEmails(requestId: string) {
    try {
      const origin = typeof window !== 'undefined' ? window.location.origin : undefined;
      const { error } = await supabase.functions.invoke('send-review-emails', {
        body: { requestId, origin }
      });

      if (error) {
        console.error('Error triggering review emails:', error);
        // Don't throw error here as it shouldn't block the status update
      }
    } catch (error) {
      console.error('Error calling review email function:', error);
      // Don't throw error here as it shouldn't block the status update
    }
  },

  async getCDMOProfile(userId: string): Promise<CDMOApplication | null> {
    const { data, error } = await supabase
      .from('cdmo_applications')
      .select('*')
      .eq('user_id', userId)
      .single();

    if (error) {
      if (error.code === 'PGRST116') { // Code for No rows found
        return null;
      }
      console.error('Error fetching CDMO profile:', error);
      throw error;
    }

    return data;
  },

  async getCDMOProfileById(cdmoId: string): Promise<CDMOApplication | null> {
    const { data, error } = await supabase
      .from('cdmo_applications')
      .select('*')
      .eq('id', cdmoId)
      .single();

    if (error) {
      if (error.code === 'PGRST116') { // Code for No rows found
        return null;
      }
      console.error('Error fetching CDMO profile by ID:', error);
      throw error;
    }

    return data;
  },

  async updateCDMOProfile(userId: string, data: Partial<CDMOApplication>) {
    const validation = validationService.validateCDMOApplication(data);
    if (!validation.isValid) {
      throw new Error(`Validation failed: ${validation.errors.join(', ')}`);
    }

    const { error } = await supabase
      .from('cdmo_applications')
      .update(data)
      .eq('user_id', userId);

    if (error) {
      console.error('Error updating CDMO profile:', error);
      throw error;
    }
  },

  async updateCDMOProfileById(cdmoId: string, data: Partial<CDMOApplication>) {
    const validation = validationService.validateCDMOApplication(data);
    if (!validation.isValid) {
      throw new Error(`Validation failed: ${validation.errors.join(', ')}`);
    }

    const { data: updatedData, error } = await supabase
      .from('cdmo_applications')
      .update(data)
      .eq('id', cdmoId)
      .select()
      .single();

    if (error) {
      console.error('Error updating CDMO profile by ID:', error);
      throw error;
    }

    return updatedData;
  },


  // Trigger acceptance email notifications (to pharma user and CDMO)
  async triggerAcceptanceEmails(requestId: string) {
    try {
      const origin = typeof window !== 'undefined' ? window.location.origin : undefined;
      const { error } = await supabase.functions.invoke('send-acceptance-emails', {
        body: { requestId, origin }
      });

      if (error) {
        console.error('Error triggering acceptance emails:', error);
        // Don't throw error here as it shouldn't block the status update
      }
    } catch (error) {
      console.error('Error calling acceptance email function:', error);
      // Don't throw error here as it shouldn't block the status update
    }
  },


  // Review functions
  async createReview(data: Review) {
    // Determine what entity is being reviewed
    const reviewPayload: any = {
      introduction_request_id: data.introduction_request_id,
      reviewer_user_id: data.reviewer_user_id,
      reviewer_type: data.reviewer_type,
      overall_rating: data.overall_rating,
      responsiveness_rating: data.responsiveness_rating,
      communication_rating: data.communication_rating,
      text_feedback: data.text_feedback || null
    };

    // Add type-specific ratings and set reviewed entity
    if (data.reviewer_type === 'startup') {
      // Startup reviewing CDMO
      reviewPayload.alignment_rating = data.alignment_rating || null;
      reviewPayload.reviewed_cdmo_id = data.reviewed_cdmo_id || null;
      reviewPayload.reviewed_user_id = null;
    } else {
      // CDMO reviewing startup
      reviewPayload.professionalism_rating = data.professionalism_rating || null;
      reviewPayload.requirements_clarity_rating = data.requirements_clarity_rating || null;
      reviewPayload.reviewed_user_id = data.reviewed_user_id || null;
      reviewPayload.reviewed_cdmo_id = null;
    }

    const { error } = await supabase
      .from('reviews')
      .insert([reviewPayload]);

    if (error) {
      console.error('Error creating review:', error);
      throw error;
    }
  },

  async getIntroductionRequestById(requestId: string) {
    const { data, error } = await supabase
      .from('introduction_requests')
      .select(`
        *,
        cdmo_applications!introduction_requests_cdmo_id_fkey (
          company_name,
          contact_name,
          user_id
        )
      `)
      .eq('id', requestId)
      .single();

    if (error) {
      console.error('Error fetching introduction request:', error);
      throw error;
    }

    return data;
  },

  async getReviewByRequestAndReviewer(requestId: string, reviewerUserId: string, reviewerType: string) {
    const { data, error } = await supabase
      .from('reviews')
      .select('*')
      .eq('introduction_request_id', requestId)
      .eq('reviewer_user_id', reviewerUserId)
      .eq('reviewer_type', reviewerType)
      .single();

    if (error && error.code !== 'PGRST116') { // PGRST116 = no rows found
      console.error('Error fetching review:', error);
      throw error;
    }
    return data;
  },

  async isCdmoUser(userId: string, cdmoId: string): Promise<boolean> {
    const { data, error } = await supabase
      .from('cdmo_applications')
      .select('id')
      .eq('id', cdmoId)
      .eq('user_id', userId)
      .single();

    if (error) {
      return false;
    }

    return !!data;
  },

  // User matching weights functions
  async getUserMatchingWeights(userId: string): Promise<UserMatchingWeights | null> {
    const { data, error } = await supabase
      .from('user_matching_weights')
      .select('*')
      .eq('user_id', userId)
      .single();

    if (error) {
      if (error.code === 'PGRST116') { // No rows found
        return null;
      }
      console.error('Error fetching user matching weights:', error);
      throw error;
    }

    return data;
  },

  async getScoringBaseWeights(): Promise<Record<string, number>> {
    const { data, error } = await supabase
      .from('scoring_base_weights')
      .select('criteria, weight');

    if (error) {
      console.error('Error fetching scoring base weights:', error);
      throw error;
    }

    return (data || []).reduce<Record<string, number>>((acc, row: ScoringBaseWeight) => {
      acc[row.criteria] = row.weight;
      return acc;
    }, {});
  },

  async saveUserMatchingWeights(userId: string, weights: Partial<UserMatchingWeights>) {
    const { error } = await supabase
      .from('user_matching_weights')
      .upsert({
        user_id: userId,
        company_name: weights.company_name,
        facility_locations_details: weights.facility_locations_details,
        primary_capabilities: weights.primary_capabilities,
        detailed_capabilities: weights.detailed_capabilities,
        engagement_scales: weights.engagement_scales,
        preferred_project_types: weights.preferred_project_types,
        typical_batch_sizes: weights.typical_batch_sizes,
        target_client_types: weights.target_client_types,
        gmp_certifications: weights.gmp_certifications,
        speed_to_start: weights.speed_to_start,
        description: weights.description,
        budget: weights.budget,
        operating_regions: weights.operating_regions,
      });

    if (error) {
      console.error('Error saving user matching weights:', error);
      throw error;
    }
  },

  async resetUserMatchingWeights(userId: string) {
    const { error } = await supabase
      .from('user_matching_weights')
      .delete()
      .eq('user_id', userId);

    if (error) {
      console.error('Error resetting user matching weights:', error);
      throw error;
    }
  },

  // Notification functions
  async getUserNotifications(userId: string) {
    const { data, error } = await supabase
      .from('notifications')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching user notifications:', error);
      throw error;
    }
    return data || [];
  },

  async getUnreadNotificationsCount(userId: string) {
    const { count, error } = await supabase
      .from('notifications')
      .select('*', { count: 'exact', head: true })
      .eq('user_id', userId)
      .eq('is_read', false);

    if (error) {
      console.error('Error fetching unread notifications count:', error);
      return 0;
    }

    return count || 0;
  },

  async markNotificationAsRead(notificationId: string) {
    const { error } = await supabase
      .from('notifications')
      .update({ is_read: true })
      .eq('id', notificationId);

    if (error) {
      console.error('Error marking notification as read:', error);
      throw error;
    }
  },

  async markAllNotificationsAsRead(userId: string) {
    const { error } = await supabase
      .from('notifications')
      .update({ is_read: true })
      .eq('user_id', userId)
      .eq('is_read', false);

    if (error) {
      console.error('Error marking all notifications as read:', error);
      throw error;
    }
  },

  // Check if user exists in custom users table
  async getUserProfile(userId: string): Promise<User | null> {
    try {
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .eq('id', userId)
        .single();

      if (error) {
        if (error.code === 'PGRST116') { // No rows found
          return null;
        }
        console.error('Error fetching user profile:', error);
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Error in getUserProfile:', error);
      return null;
    }
  },

  // Check if user is an admin by email
  async isUserAdmin(email: string): Promise<boolean> {
    try {
      const { data, error } = await supabase
        .from('admin')
        .select('email')
        .eq('email', email.toLowerCase().trim())
        .single();

      if (error) {
        if (error.code === 'PGRST116') { // No rows found
          return false;
        }
        console.error('Error checking admin status:', error);
        return false;
      }

      return !!data;
    } catch (error) {
      console.error('Error in isUserAdmin:', error);
      return false;
    }
  },

  /**
   * Whether this admin is the superadmin — the only role allowed to manage other admins.
   *
   * Kept separate from {@link isUserAdmin} rather than folded into it: that method is called from
   * half a dozen places and mocked across the test suite, and widening its return type would ripple
   * further than this feature warrants. AuthContext only calls this once an admin is confirmed, so
   * the extra round-trip never happens for ordinary users.
   *
   * Read-only and advisory. The server re-checks the flag on every privileged action, so a false
   * positive here would reveal a tab, not grant any power.
   */
  async isSuperadmin(email: string): Promise<boolean> {
    try {
      const { data, error } = await supabase
        .from('admin')
        .select('is_superadmin')
        .eq('email', email.toLowerCase().trim())
        .maybeSingle();

      if (error) {
        console.error('Error checking superadmin status:', error);
        return false;
      }

      return Boolean(data?.is_superadmin);
    } catch (error) {
      console.error('Error in isSuperadmin:', error);
      return false;
    }
  },

  /**
   * 'pending' = invited but has never signed in; 'active' = has signed in at least once; null when
   * the email is not an admin at all. This is the row's own status, not a derived guess — the
   * signal PasswordResetPage needs to tell an admin invite from an ordinary password reset.
   */
  async getAdminStatus(email: string): Promise<'pending' | 'active' | null> {
    try {
      const { data, error } = await supabase
        .from('admin')
        .select('status')
        .eq('email', email.toLowerCase().trim())
        .maybeSingle();

      if (error) {
        console.error('Error checking admin status:', error);
        return null;
      }

      return (data?.status as 'pending' | 'active' | undefined) ?? null;
    } catch (error) {
      console.error('Error in getAdminStatus:', error);
      return null;
    }
  },

  // Chat system functions
  async createConversation(introductionRequestId: string, startupUserId: string, cdmoUserId: string, cdmoId: string): Promise<Conversation> {
    const { data, error } = await supabase
      .from('conversations')
      .insert([{
        introduction_request_id: introductionRequestId,
        startup_user_id: startupUserId,
        cdmo_user_id: cdmoUserId,
        cdmo_id: cdmoId
      }])
      .select()
      .single();

    if (error) {
      console.error('Error creating conversation:', error);
      throw error;
    }

    return data;
  },

  async getConversationByIntroductionRequest(introductionRequestId: string): Promise<Conversation | null> {
    const { data, error } = await supabase
      .from('conversations')
      .select('*')
      .eq('introduction_request_id', introductionRequestId)
      .single();

    if (error) {
      if (error.code === 'PGRST116') { // No rows found
        return null;
      }
      console.error('Error fetching conversation:', error);
      throw error;
    }

    return data;
  },

  async getConversationById(conversationId: string): Promise<Conversation | null> {
    const { data, error } = await supabase
      .from('conversations')
      .select('*')
      .eq('id', conversationId)
      .single();

    if (error) {
      if (error.code === 'PGRST116') { // No rows found
        return null;
      }
      console.error('Error fetching conversation by ID:', error);
      throw error;
    }

    return data;
  },

  async getUserConversations(userId: string): Promise<ConversationWithDetails[]> {
    const { data, error } = await supabase
      .from('conversations')
      .select(`
        *,
        cdmo_applications!conversations_cdmo_id_fkey (
          company_name,
          contact_name
        ),
        users_startup:users!conversations_startup_user_id_fkey (
          name,
          company
        ),
        users_cdmo:users!conversations_cdmo_user_id_fkey (
          name,
          company
        ),
        introduction_request:introduction_requests!conversations_introduction_request_id_fkey (
          shared_field_settings,
          shared_project_brief
        )
      `)
      .or(`startup_user_id.eq.${userId},cdmo_user_id.eq.${userId}`)
      .order('updated_at', { ascending: false });

    if (error) {
      console.error('Error fetching user conversations:', error);
      throw error;
    }

    // Get unread counts and last messages for each conversation
    const conversationsWithDetails = await Promise.all(
      (data || []).map(async (conversation) => {
        const [unreadCount, lastMessage] = await Promise.all([
          this.getUnreadMessageCount(conversation.id, userId),
          this.getLastMessage(conversation.id)
        ]);

        const introRequest = conversation.introduction_request as
          | ConversationWithDetails['introduction_request']
          | undefined;
        const sharedFieldSettings = introRequest?.shared_field_settings ?? null;
        const sharedProjectBrief = introRequest?.shared_project_brief ?? null;
        const sponsorDisplayName = getSponsorDisplayName(sharedFieldSettings, sharedProjectBrief);

        const sanitizedConversation: ConversationWithDetails = {
          ...conversation,
          unread_count: unreadCount,
          last_message: lastMessage,
          sponsor_display_name: sponsorDisplayName,
        };

        if (conversation.cdmo_user_id === userId && !sharedFieldSettings?.companyName) {
          sanitizedConversation.users_startup = {
            name: '',
            company: sponsorDisplayName,
          };
        }

        return sanitizedConversation;
      })
    );

    // Sort conversations by last message timestamp (most recent first)
    // If no last message, use conversation updated_at as fallback
    return conversationsWithDetails.sort((a, b) => {
      const aTime = a.last_message?.created_at || a.updated_at;
      const bTime = b.last_message?.created_at || b.updated_at;
      return new Date(bTime).getTime() - new Date(aTime).getTime();
    });
  },

  async getConversationMessages(conversationId: string): Promise<Message[]> {
    const { data, error } = await supabase
      .from('messages')
      .select('*')
      .eq('conversation_id', conversationId)
      .order('created_at', { ascending: true });

    if (error) {
      console.error('Error fetching conversation messages:', error);
      throw error;
    }

    return data || [];
  },

  async sendMessage(conversationId: string, senderId: string, content: string): Promise<Message> {
    console.log(`=== SENDING MESSAGE ===`);
    console.log(`Conversation ID: ${conversationId}`);
    console.log(`Sender ID: ${senderId}`);
    console.log(`Content: ${content.substring(0, 50)}...`);

    const { data, error } = await supabase
      .from('messages')
      .insert([{
        conversation_id: conversationId,
        sender_id: senderId,
        content: content.trim(),
        is_read: false,
        read_at: null // Messages start as unread
      }])
      .select()
      .single();

    if (error) {
      console.error('❌ Error sending message:', error);
      throw error;
    }

    console.log(`✅ Message sent successfully: ${data.id}`);
    console.log(`=== END SENDING MESSAGE ===`);
    return data;
  },

  async markMessagesAsRead(conversationId: string, userId: string): Promise<void> {
    console.log(`=== MARKING MESSAGES AS READ ===`);
    console.log(`Conversation ID: ${conversationId}`);
    console.log(`Current User ID: ${userId}`);

    // First, let's see what messages exist in this conversation
    const { data: allMessages, error: fetchError } = await supabase
      .from('messages')
      .select('id, sender_id, read_at, is_read, content')
      .eq('conversation_id', conversationId);

    if (fetchError) {
      console.error('❌ Error fetching messages:', fetchError);
      return;
    }

    console.log(`📋 All messages in conversation (${allMessages?.length || 0}):`);
    if (allMessages) {
      allMessages.forEach(msg => {
        console.log(`  - Message ID: ${msg.id}, Sender: ${msg.sender_id}, Read: ${msg.is_read}, Read at: ${msg.read_at}, Content: ${msg.content?.substring(0, 30)}...`);
      });
    }

    // Now let's see what messages would be updated (from other user)
    const { data: messagesToUpdate, error: filterError } = await supabase
      .from('messages')
      .select('id, sender_id, read_at, is_read')
      .eq('conversation_id', conversationId)
      .neq('sender_id', userId);

    if (filterError) {
      console.error('❌ Error filtering messages:', filterError);
      return;
    }

    console.log(`🎯 Messages from other user (${messagesToUpdate?.length || 0}):`);
    if (messagesToUpdate) {
      messagesToUpdate.forEach(msg => {
        console.log(`  - Message ID: ${msg.id}, Sender: ${msg.sender_id}, Read: ${msg.is_read}, Read at: ${msg.read_at}`);
      });
    }

    // Now update all messages from other user
    console.log(`🔄 Attempting to update messages...`);
    console.log(`   - Conversation ID: ${conversationId}`);
    console.log(`   - Excluding sender ID: ${userId}`);
    console.log(`   - Update values: is_read=true, read_at=${new Date().toISOString()}`);

    const { data, error } = await supabase
      .from('messages')
      .update({
        is_read: true,
        read_at: new Date().toISOString()
      })
      .eq('conversation_id', conversationId)
      .neq('sender_id', userId)
      .is('read_at', null) // Only update messages that haven't been read yet
      .select('id, sender_id, read_at, is_read');

    if (error) {
      console.error('❌ Error marking messages as read:', error);
      console.error('❌ Error details:', error.message, error.details, error.hint);
      throw error;
    }

    console.log(`✅ Updated ${data?.length || 0} messages:`);
    if (data) {
      data.forEach(msg => {
        console.log(`  - Message ID: ${msg.id}, Sender: ${msg.sender_id}, Read: ${msg.is_read}, Read at: ${msg.read_at}`);
      });
    } else {
      console.log(`⚠️  No messages were updated despite finding ${messagesToUpdate?.length || 0} messages from other user`);
    }
    console.log(`=== END MARKING MESSAGES ===`);
  },

  async getUnreadMessageCount(conversationId: string, userId: string): Promise<number> {
    const { count, error } = await supabase
      .from('messages')
      .select('*', { count: 'exact', head: true })
      .eq('conversation_id', conversationId)
      .neq('sender_id', userId)
      .is('read_at', null);

    if (error) {
      console.error('Error getting unread message count:', error);
      return 0;
    }

    return count || 0;
  },

  async getOrCreateConversation(introductionRequestId: string, startupUserId: string, cdmoUserId: string, cdmoId: string): Promise<Conversation> {
    // First try to get existing conversation
    const existingConversation = await this.getConversationByIntroductionRequest(introductionRequestId);

    if (existingConversation) {
      return existingConversation;
    }

    // Create new conversation if it doesn't exist
    return await this.createConversation(introductionRequestId, startupUserId, cdmoUserId, cdmoId);
  },

  async getLastMessage(conversationId: string): Promise<Message | null> {
    const { data, error } = await supabase
      .from('messages')
      .select('*')
      .eq('conversation_id', conversationId)
      .order('created_at', { ascending: false })
      .limit(1)
      .single();

    if (error) {
      if (error.code === 'PGRST116') { // No rows found
        return null;
      }
      console.error('Error fetching last message:', error);
      return null;
    }

    return data;
  },

  // Debug function to reset all messages to unread (for testing)
  async resetAllMessagesToUnread(): Promise<void> {
    console.log('Resetting all messages to unread...');

    const { data, error } = await supabase
      .from('messages')
      .update({
        is_read: false,
        read_at: null
      })
      .select('id');

    if (error) {
      console.error('Error resetting messages:', error);
      throw error;
    }

    console.log(`Reset ${data?.length || 0} messages to unread`);
  },

  // Debug function to check database structure
  async checkDatabaseStructure(): Promise<void> {
    console.log('=== CHECKING DATABASE STRUCTURE ===');

    // Check if messages table exists and has the right columns
    const { data: messages, error: messagesError } = await supabase
      .from('messages')
      .select('*')
      .limit(1);

    if (messagesError) {
      console.error('❌ Error accessing messages table:', messagesError);
    } else {
      console.log('✅ Messages table accessible');
      if (messages && messages.length > 0) {
        const sampleMessage = messages[0];
        console.log('📋 Sample message structure:', Object.keys(sampleMessage));
        console.log('📋 Sample message:', sampleMessage);
      }
    }

    // Check total message count
    const { count, error: countError } = await supabase
      .from('messages')
      .select('*', { count: 'exact', head: true });

    if (countError) {
      console.error('❌ Error counting messages:', countError);
    } else {
      console.log(`📊 Total messages in database: ${count}`);
    }

    // Test a simple UPDATE query
    if (messages && messages.length > 0) {
      const testMessageId = messages[0].id;
      console.log(`🧪 Testing UPDATE on message ID: ${testMessageId}`);

      const { data: updateTest, error: updateError } = await supabase
        .from('messages')
        .update({
          is_read: true,
          read_at: new Date().toISOString()
        })
        .eq('id', testMessageId)
        .select('id, is_read, read_at');

      if (updateError) {
        console.error('❌ UPDATE test failed:', updateError);
        console.error('❌ UPDATE error details:', updateError.message, updateError.details, updateError.hint);
      } else {
        console.log('✅ UPDATE test successful:', updateTest);
      }
    }

    console.log('=== END DATABASE STRUCTURE CHECK ===');
  },

  // Mark individual message as read
  async markMessageAsRead(messageId: string): Promise<void> {
    console.log(`=== MARKING SINGLE MESSAGE AS READ ===`);
    console.log(`Message ID: ${messageId}`);

    // Only update if the message hasn't been read yet
    const { data, error } = await supabase
      .from('messages')
      .update({
        is_read: true,
        read_at: new Date().toISOString()
      })
      .eq('id', messageId)
      .is('read_at', null) // Only update if not already read
      .select('id, sender_id, read_at');

    if (error) {
      console.error('❌ Error marking message as read:', error);
      throw error;
    }

    if (data && data.length > 0) {
      console.log(`✅ Message updated:`, data[0]);
    } else {
      console.log(`ℹ️  Message was already read or doesn't exist`);
    }
    console.log(`=== END MARKING SINGLE MESSAGE ===`);
  },

  // CDMO Ownership Management
  async initiateCDMOOwnershipTransfer(cdmoId: string, senderId: string, senderEmail: string, senderName: string, receiverEmail: string, receiverName: string, receiverPhone: string, companyName: string) {
    // Get CDMO details
    const { data: cdmo, error: cdmoError } = await supabase
      .from('cdmo_applications')
      .select('id, company_name, user_id')
      .eq('id', cdmoId)
      .eq('user_id', senderId)
      .single();

    if (cdmoError || !cdmo) {
      throw new Error('CDMO application not found or you do not have permission to transfer it.');
    }

    // Check if the receiver email already exists in cdmo_applications
    const { data: existingCDMO } = await supabase
      .from('cdmo_applications')
      .select('id')
      .neq('id', cdmoId)
      .eq('email', receiverEmail.toLowerCase().trim())
      .maybeSingle();

    if (existingCDMO) {
      throw new Error('This email is already linked to an existing CDMO profile.');
    }

    // Note: Multiple transfers can now be initiated. First come first served.

    // Get current origin for local/staging support
    const origin = typeof window !== 'undefined' ? window.location.origin : undefined;

    // Call Edge Function to send transfer email and create transfer record
    const { data, error } = await supabase.functions.invoke('send-ownership-transfer', {
      body: {
        cdmoId,
        senderEmail,
        senderName,
        receiverEmail: receiverEmail?.toLowerCase().trim(),
        receiverName,
        receiverPhone,
        companyName: companyName || cdmo.company_name,
        origin
      }
    });

    if (error) {
      console.error('Error sending ownership transfer email:', error);
      throw new Error(CDMO_OWNERSHIP_CONSTANTS.INITIATE_TRANSFER_FAILED);
    }

    return data;
  },

  async unclaimCDMO(cdmoId: string, userId: string) {
    // Generate new tokens for re-claiming
    const newClaimToken = crypto.randomUUID();
    const newAdminClaimToken = crypto.randomUUID();
    const expiresAt = new Date();
    expiresAt.setDate(expiresAt.getDate() + 14); // 14 days expiry

    // 1. Fetch details for notifications before clearing ownership
    const { data: cdmoData } = await supabase
      .from('cdmo_applications')
      .select('company_name, primary_email, secondary_emails')
      .eq('id', cdmoId)
      .eq('user_id', userId)
      .single();

    const { data: userData } = await supabase
      .from('users')
      .select('name, email')
      .eq('id', userId)
      .single();

    const userEmail = userData?.email?.toLowerCase().trim();
    let newPrimaryEmail = cdmoData?.primary_email;
    if (newPrimaryEmail?.toLowerCase().trim() === userEmail) {
      newPrimaryEmail = null;
    }

    const newSecondaryEmails = (cdmoData?.secondary_emails || []).filter(
      (e: string) => e?.toLowerCase().trim() !== userEmail
    );

    // 2. Cancel any pending ownership transfers for this profile — must run before
    // cdmo_applications.user_id is cleared below, since the owner-scoped RLS policy on
    // cdmo_ownership_transfers authorizes this write by checking that the caller still
    // owns the CDMO application at the time this UPDATE executes.
    try {
      await supabase
        .from('cdmo_ownership_transfers')
        .update({ status: 'expired' })
        .eq('cdmo_id', cdmoId)
        .eq('status', 'pending');
    } catch (transferErr) {
      console.warn('Error clearing pending transfers during unclaim:', transferErr);
    }

    // 3. Update the CDMO application to unclaimed status and clear claimant data
    const { error: cdmoError } = await supabase
      .from('cdmo_applications')
      .update({
        user_id: null,
        status: CDMO_STATUS.UNCLAIMED,
        contact_name: null,
        email: null,
        primary_email: newPrimaryEmail,
        secondary_emails: newSecondaryEmails,
        phone: null,
        claim_token: newClaimToken,
        claim_token_expires_at: expiresAt.toISOString(),
        admin_claim_token: newAdminClaimToken,
        last_reminder_sent_at: null,
        reminder_cycle_start_at: new Date().toISOString(), // Restart reminder cycle
        campaign_active: false,
        claim_email_stage: 0,
        last_email_sent_at: null,
        claim_campaign_started_at: null,
      })
      .eq('id', cdmoId)
      .eq('user_id', userId);

    if (cdmoError) {
      console.error('Error unclaiming CDMO profile:', cdmoError);
      throw cdmoError;
    }

    // 4. Convert the user's account to a biotech (startup) account
    const { error: userError } = await supabase
      .from('users')
      .update({
        role: 'startup'
      })
      .eq('id', userId);

    if (userError) {
      console.warn('Error updating user role during unclaim:', userError);
    }

    // 5. Trigger notifications (Admin + CDMO Contacts)
    if (cdmoData && userData) {
      const origin = typeof window !== 'undefined' ? window.location.origin : undefined;
      void supabase.functions.invoke('send-unclaim-notifications', {
        body: {
          cdmoId,
          userId,
          userEmail: userData.email,
          userName: userData.name,
          companyName: cdmoData.company_name,
          newClaimToken,
          origin
        }
      }).catch(err => console.warn('Failed to trigger unclaim notifications:', err));
    }

    // 6. Re-trigger notifications for ALL pending introduction requests
    // This ensures that all existing leads are "redirected" to the primary/secondary emails
    // try {
    //   const { data: pendingRequests } = await supabase
    //     .from('introduction_requests')
    //     .select('*')
    //     .eq('cdmo_id', cdmoId)
    //     .eq('status', 'pending');

    //   if (pendingRequests && pendingRequests.length > 0) {
    //     // Trigger notifications for all pending requests
    //     // The trigger logic will now see user_id is null and send to primary/secondary contacts
    //     await Promise.all(pendingRequests.map(req => this.triggerCDMONotification(req)));
    //   }
    // } catch (notifErr) {
    //   console.warn('Error re-triggering notifications after unclaim:', notifErr);
    // }

    return newClaimToken;
  },

  // Completes an ownership transfer via the service-role complete-ownership-transfer
  // edge function — the caller here is often anonymous (pre-email-confirmation signup),
  // so this must never touch cdmo_ownership_transfers/cdmo_applications/users directly.
  async completeOwnershipTransfer(
    transferToken: string,
    newUserId: string,
    transferEmail: string,
    newUserName?: string
  ) {
    const { data, error } = await supabase.functions.invoke('complete-ownership-transfer', {
      body: { action: 'complete', token: transferToken, newUserId, email: transferEmail, newUserName },
    });

    if (error) {
      console.error('Error completing ownership transfer:', error);
      throw new Error(CDMO_OWNERSHIP_CONSTANTS.TRANSFER_NOT_FOUND);
    }

    if (!data?.success) {
      switch (data?.code) {
        case 'TRANSFER_NOT_FOUND':
        case 'TRANSFER_NOT_PENDING':
          throw new Error(CDMO_OWNERSHIP_CONSTANTS.TRANSFER_NOT_FOUND);
        case 'TRANSFER_TOKEN_EXPIRED':
          throw new Error(VALIDATION_MESSAGES.TRANSFER_TOKEN_EXPIRED);
        case 'TRANSFER_PROFILE_ALREADY_CLAIMED':
          throw new Error(VALIDATION_MESSAGES.TRANSFER_PROFILE_ALREADY_CLAIMED);
        case 'TRANSFER_RECIPIENT_MISMATCH':
          throw new Error(VALIDATION_MESSAGES.TRANSFER_RECIPIENT_MISMATCH(data?.receiverEmail || ''));
        default:
          throw new Error(VALIDATION_MESSAGES.TRANSACTION_ERROR);
      }
    }

    return { previousOwnerId: data.previousOwnerId, newOwnerId: data.newOwnerId, cdmoId: data.cdmoId };
  },

  // Fetch ownership transfer details by verification token (used to prefill transfer signup).
  // Routed through the same edge function since this is called pre-authentication too.
  async getOwnershipTransferByToken(token: string) {
    try {
      const { data, error } = await supabase.functions.invoke('complete-ownership-transfer', {
        body: { action: 'lookup', token },
      });

      if (error || !data?.success) {
        if (error) console.error('Error fetching ownership transfer by token:', error);
        return null;
      }

      return data.transfer ?? null;
    } catch (error) {
      console.error('getOwnershipTransferByToken error:', error);
      return null;
    }
  },

  // Enable real-time for introduction_requests table (if not already enabled)
  async enableRealtimeForIntroductionRequests(): Promise<void> {
    try {
      console.log('🔧 Attempting to enable real-time for introduction_requests table...');

      // This is a SQL command that should be run in the Supabase SQL editor
      // We'll just log it here for manual execution
      const sqlCommand = `
        -- Realtime must use a column list (PG 15+) so WAL decoding does not leak PII columns.
        ALTER PUBLICATION supabase_realtime DROP TABLE introduction_requests;
        ALTER PUBLICATION supabase_realtime ADD TABLE introduction_requests (
          id,
          requester_user_id,
          cdmo_id,
          status,
          created_at,
          shared_field_settings,
          shared_project_brief
        );
      `;

      console.log('📝 SQL Command to run in Supabase SQL Editor:');
      console.log(sqlCommand);
      console.log('⚠️  Please run this command in your Supabase SQL Editor to enable real-time');

    } catch (error) {
      console.error('Error enabling real-time:', error);
    }
  },

  /**
   * Admin Force Transfer: Allows an admin to reassign a CDMO profile's ownership
   * by changing the email on the Company Information page.
   *
   * Steps:
   * 1. Check if newEmail is already owner of another CDMO profile
   * 2. Unlink current owner (convert role to 'startup')
   * 3. Link new owner if account exists (set role to 'cdmo')
   * 4. Update CDMO profile with new email + user_id
   * 5. Record the transfer
   * 6. Send notification emails
   */
  async adminForceTransfer(
    cdmoId: string,
    newEmail: string,
    adminEmail: string,
  ): Promise<{ success: boolean; message: string }> {
    const normalizedNewEmail = newEmail.trim().toLowerCase();

    // Get current origin for local/staging support (same pattern as normal transfer)
    const origin = typeof window !== 'undefined' ? window.location.origin : undefined;

    // ── 1. Fetch the CDMO profile being transferred ──
    const { data: cdmoProfile, error: cdmoError } = await supabase
      .from('cdmo_applications')
      .select('id, company_name, user_id, email, contact_name')
      .eq('id', cdmoId)
      .single();

    if (cdmoError || !cdmoProfile) {
      throw new Error(VALIDATION_MESSAGES.CDMO_PROFILE_NOT_FOUND);
    }

    const companyName = cdmoProfile?.company_name ?? "";
    const previousOwnerId = cdmoProfile?.user_id ?? null;
    const previousEmail = cdmoProfile?.email ?? "";

    // ── 2. Fetch previous owner details ──
    let previousOwnerName = cdmoProfile?.contact_name ?? '';
    let previousOwnerEmail = previousEmail ?? '';

    if (previousOwnerId) {
      const { data: prevUser } = await supabase
        .from('users')
        .select('name, email')
        .eq('id', previousOwnerId)
        .single();

      if (prevUser) {
        previousOwnerName = prevUser?.name ?? previousOwnerName;
        previousOwnerEmail = prevUser?.email ?? previousOwnerEmail;
      }
    }

    // ── 3. Check if newEmail is already owner of another CDMO ──
    const { data: existingCdmo } = await supabase
      .from('cdmo_applications')
      .select('id, company_name, user_id')
      .eq('email', normalizedNewEmail)
      .neq('id', cdmoId)
      .maybeSingle();

    if (existingCdmo && existingCdmo.user_id) {
      throw new Error(
        VALIDATION_MESSAGES.ADMIN_FORCE_TRANSFER_EMAIL_ALREADY_ASSOCIATED(existingCdmo.company_name)
      );
    }

    // ── 4. Look up the new owner in the users table ──
    const { data: newUser } = await supabase
      .from('users')
      .select('id, name, email, role')
      .eq('email', normalizedNewEmail)
      .maybeSingle();

    const newOwnerHasAccount = !!newUser;
    const newOwnerId = newUser?.id ?? null;
    const newOwnerName = newUser?.name ?? null;

    // ── 5. Unlink current owner — convert to startup role ──
    if (previousOwnerId) {
      const { error: roleError } = await supabase
        .from('users')
        .update({ role: 'startup' })
        .eq('id', previousOwnerId);

      if (roleError) {
        console.warn('Warning: Could not convert previous owner role:', roleError);
      }
    }

    // ── 6. If new owner has account, set role to 'cdmo' ──
    if (newOwnerId) {
      const { error: newRoleError } = await supabase
        .from('users')
        .update({ role: 'cdmo' })
        .eq('id', newOwnerId);

      if (newRoleError) {
        console.warn('Warning: Could not set new owner role:', newRoleError);
      }
    }

    // ── 7. Update CDMO profile with new email + user_id ──
    const updatePayload: any = {
      email: normalizedNewEmail,
      primary_email: normalizedNewEmail,
      user_id: newOwnerId, // null if no account yet
      status: 'claimed',
    };

    // If new owner has account, update contact name to their name
    if (newOwnerName) {
      updatePayload.contact_name = newOwnerName;
    }

    const { error: updateError } = await supabase
      .from('cdmo_applications')
      .update(updatePayload)
      .eq('id', cdmoId);

    if (updateError) {
      console.error('Error updating CDMO profile:', updateError);
      throw new Error(VALIDATION_MESSAGES.ADMIN_FORCE_TRANSFER_FAILED);
    }

    // ── 8. Record the transfer in cdmo_ownership_transfers ──
    // If new owner has no account, set status to 'pending' so they can claim via signup
    // just like the normal CDMO-to-CDMO transfer flow
    const transferToken = crypto.randomUUID();
    const transferDate = new Date().toISOString();
    try {
      await supabase
        .from('cdmo_ownership_transfers')
        .insert({
          cdmo_id: cdmoId,
          sender_id: previousOwnerId || null,
          sender_email: previousOwnerEmail,
          receiver_email: normalizedNewEmail,
          receiver_name: newOwnerName || null,
          receiver_id: newOwnerId || null,
          company_name: companyName,
          verification_token: transferToken,
          status: newOwnerHasAccount ? 'completed' : 'pending',
          completed_at: newOwnerHasAccount ? transferDate : null,
          initiated_by_admin: true,
        });
    } catch (transferLogError) {
      console.warn('Warning: Could not log the transfer record:', transferLogError);
    }

    // ── 9. Cancel all other pending transfers for this CDMO ──
    try {
      await supabase
        .from('cdmo_ownership_transfers')
        .update({ status: 'expired' })
        .eq('cdmo_id', cdmoId)
        .eq('status', 'pending')
        .neq('verification_token', transferToken);
    } catch (expireError) {
      console.warn('Warning: Could not expire pending transfers:', expireError);
    }

    // ── 10. Send notification emails ──
    try {
      await supabase.functions.invoke('send-admin-force-transfer-emails', {
        body: {
          companyName,
          previousOwnerEmail,
          previousOwnerName,
          newOwnerEmail: normalizedNewEmail,
          newOwnerName,
          newOwnerHasAccount,
          adminEmail,
          transferDate,
          // Pass origin, transferToken, and cdmoId so the edge function
          // can build correct links (localhost for dev, staging for stage)
          origin,
          transferToken,
          cdmoId,
        }
      });
    } catch (emailError) {
      console.warn('Warning: Notification emails failed:', emailError);
    }

    return {
      success: true,
      message: VALIDATION_MESSAGES.ADMIN_FORCE_TRANSFER_SUCCESS,
    };
  },

  /**
   * Admin Force Unclaim: Allows an admin to forcibly unclaim a CDMO profile.
   *
   * Steps:
   * 1. Fetch the current owner details
   * 2. Unlink the user (set user_id = null, role back to 'startup')
   * 3. Clear email, contact_name from the profile
   * 4. Generate a new permanent claim link (admin_claim_token + claim_token)
   * 5. Update status to 'unclaimed'
   * 6. Send notification email to admin
   */
  async adminForceUnclaim(
    cdmoId: string,
    adminEmail: string,
  ): Promise<{ success: boolean; adminClaimToken: string; message: string }> {
    // ── 1. Fetch the CDMO profile ──
    const { data: cdmoProfile, error: cdmoError } = await supabase
      .from('cdmo_applications')
      .select('id, company_name, user_id, email, contact_name, primary_email, secondary_emails')
      .eq('id', cdmoId)
      .single();

    if (cdmoError || !cdmoProfile) {
      throw new Error(VALIDATION_MESSAGES.CDMO_PROFILE_NOT_FOUND);
    }

    const companyName = cdmoProfile.company_name ?? '';
    const previousOwnerId = cdmoProfile.user_id ?? null;
    const previousEmail = cdmoProfile.email ?? '';
    const previousContactName = cdmoProfile.contact_name ?? '';

    // ── 2. Fetch previous owner name from users table ──
    let previousOwnerName = previousContactName;
    if (previousOwnerId) {
      const { data: prevUser } = await supabase
        .from('users')
        .select('name')
        .eq('id', previousOwnerId)
        .single();
      if (prevUser?.name) {
        previousOwnerName = prevUser.name;
      }
    }

    // ── 3. Downgrade previous owner from 'cdmo' → 'startup' ──
    if (previousOwnerId) {
      await supabase
        .from('users')
        .update({ role: 'startup' })
        .eq('id', previousOwnerId);
    }

    // ── 4. Generate new tokens ──
    const newClaimToken = crypto.randomUUID();
    const newAdminClaimToken = crypto.randomUUID();
    const claimExpiresAt = (() => {
      const d = new Date();
      d.setDate(d.getDate() + 14);
      return d.toISOString();
    })();

    // ── 5. Update CDMO profile: clear owner-specific fields, set unclaimed ──
    const normalizedPreviousEmail = previousEmail?.toLowerCase().trim();
    let newPrimaryEmail = cdmoProfile.primary_email;
    if (newPrimaryEmail?.toLowerCase().trim() === normalizedPreviousEmail) {
      newPrimaryEmail = null;
    }

    const newSecondaryEmails = (cdmoProfile.secondary_emails || []).filter(
      (e: string) => e?.toLowerCase().trim() !== normalizedPreviousEmail
    );

    const { error: updateError } = await supabase
      .from('cdmo_applications')
      .update({
        user_id: null,
        email: null,
        contact_name: null,
        status: CDMO_STATUS.UNCLAIMED,
        claim_token: newClaimToken,
        admin_claim_token: newAdminClaimToken,
        claim_token_expires_at: claimExpiresAt,
        primary_email: newPrimaryEmail,
        secondary_emails: newSecondaryEmails,
        campaign_active: false,
        claim_email_stage: 0,
        last_email_sent_at: null,
        claim_campaign_started_at: null,
      })
      .eq('id', cdmoId);

    if (updateError) {
      console.error('adminForceUnclaim update error:', updateError);
      throw new Error(VALIDATION_MESSAGES.ADMIN_FORCE_UNCLAIM_FAILED);
    }

    // ── 6. Get admin origin for claim link ──
    const origin = typeof window !== 'undefined' ? window.location.origin : undefined;

    // ── 7. Send admin notification email ──
    try {
      await supabase.functions.invoke('send-admin-force-unclaim-emails', {
        body: {
          cdmoId,
          companyName,
          previousOwnerEmail: previousEmail,
          previousOwnerName,
          adminEmail,
          newAdminClaimToken,
          origin,
        }
      });
    } catch (emailError) {
      console.warn('Warning: Force unclaim notification email failed:', emailError);
    }

    return {
      success: true,
      adminClaimToken: newAdminClaimToken,
      message: VALIDATION_MESSAGES.ADMIN_FORCE_UNCLAIM_SUCCESS,
    };
  },

  /**
   * Checks if an FEI number already exists in any CDMO profile.
   * Since FEI numbers are stored as comma-separated strings, we check for the value
   * as a whole item in the list.
   * 
   * @param feiNumber The FEI number to check
   * @param excludeCdmoId Optional ID to exclude from the check (when editing)
   * @returns True if already exists, false otherwise
   */
  async checkFeiNumberExists(feiNumber: string, excludeCdmoId?: string): Promise<boolean> {
    if (!feiNumber || !feiNumber.trim()) return false;
    
    const trimmed = feiNumber.trim();
    
    let query = supabase
      .from('cdmo_applications')
      .select('id, fei_number')
      .ilike('fei_number', `%${trimmed}%`);

    if (excludeCdmoId) {
      query = query.neq('id', excludeCdmoId);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error checking FEI number existence:', error);
      return false;
    }

    if (!data || data.length === 0) return false;

    // Filter results locally to ensure precise matches in the comma-separated list
    return data.some(row => {
      if (!row.fei_number) return false;
      const feis = row.fei_number.split(',').map((f: string) => f.trim());
      return feis.includes(trimmed);
    });
  },

  // Supabase client reference for custom queries
  supabase,

  /**
   * Submit an expert guidance request (saved in DB + email to nick via edge function).
   * Caller identity comes from the Supabase session JWT only — userId is not sent in the body.
   * @param payload - Form fields and optional sponsor context from auth/profile
   * @returns Inserted request id
   */
  async submitExpertGuidanceRequest(
    payload: ExpertGuidanceRequestPayload
  ): Promise<{ id: string }> {
    const { data, error } = await supabase.functions.invoke('send-expert-guidance-request', {
      body: {
        sponsorName: payload.sponsorName,
        sponsorEmail: payload.sponsorEmail,
        sponsorCompany: payload.sponsorCompany,
        helpTypes: payload.helpTypes,
        otherHelpText: payload.otherHelpText,
        projectCompanyName: payload.projectCompanyName,
        briefDescription: payload.briefDescription,
        assistanceTimeline: payload.assistanceTimeline,
        preferredFollowUpEmail: payload.preferredFollowUpEmail,
        submittedFromPage: payload.submittedFromPage ?? '/matchmaking',
      },
    });

    const result = data as { success?: boolean; id?: string; error?: string } | null;

    if (error) {
      console.error('Error submitting expert guidance request:', error);
      const detail =
        result?.error ||
        (await parseExpertGuidanceFunctionError(error)) ||
        error.message;
      throw new Error(detail || 'Failed to submit expert guidance request');
    }

    if (!result?.success || !result.id) {
      throw new Error(result?.error || 'Failed to submit expert guidance request');
    }

    return { id: result.id };
  },
};

// Password reset service functions with enhanced validation
export const passwordResetService = {
  // Request password reset with rate limiting and validation
  async requestPasswordReset(email: string) {
    try {
      // Frontend validation
      const emailValidation = validateEmail(email);
      if (!emailValidation.isValid) {
        throw new Error(emailValidation.error);
      }
      const redirectUrl = import.meta.env.VITE_APP_URL;

      // Use localhost:5173 for development, production URL for production

      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${redirectUrl}reset-password`
      });

      if (error) {
        throw error;
      }


      return { success: true };
    } catch (error: any) {
      console.error('Error requesting password reset:', error);
      throw error;
    }
  },

  // Update password with validation
  async updatePassword(newPassword: string) {
    try {
      // Validate password
      const passwordValidation = validatePassword(newPassword);
      if (!passwordValidation.isValid) {
        throw new Error(passwordValidation.error);
      }

      const { error } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (error) {
        throw error;
      }

      return { success: true };
    } catch (error: any) {
      console.error('Error updating password:', error);
      throw error;
    }
  },

  // Verify if user has valid session for password reset
  async verifyResetSession() {
    try {
      const { data: { session }, error } = await supabase.auth.getSession();

      if (error) {
        throw error;
      }

      return { session, isValid: !!session };
    } catch (error: any) {
      console.error('Error verifying reset session:', error);
      return { session: null, isValid: false };
    }
  },
};

// Email confirmation service functions
export const emailConfirmationService = {
  /**
   * Resends email confirmation for user signup
   * @param email - User's email address
   * @returns Promise with success status
   */
  async resendConfirmationEmail(email: string) {
    try {
      // Frontend validation
      const emailValidation = validateEmail(email);
      if (!emailValidation.isValid) {
        throw new Error(emailValidation.error);
      }

      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: email,
        options: {
          emailRedirectTo: `${import.meta.env.VITE_APP_URL}confirm-email`
        }
      });

      if (error) {
        throw error;
      }

      return { success: true };
    } catch (error: any) {
      console.error('Error resending confirmation email:', error);
      throw error;
    }
  },
};

/** One admin as shown on the Campaign → Admin access tab. */
export interface AdminAccessRecord {
  id: string;
  email: string;
  is_superadmin: boolean;
  status: 'pending' | 'active';
  invited_by: string | null;
  invited_at: string | null;
  activated_at: string | null;
  created_at: string | null;
  /** auth.users id. Null only if the account was deleted after the grant. */
  user_id: string | null;
}

/**
 * Thin wrappers over the `manage-admin-access` edge function.
 *
 * Every call is authorised server-side against the caller's `is_superadmin` flag — these helpers
 * carry no privilege of their own, and calling them as a non-superadmin returns 403. Nothing here
 * writes to the `admin` table directly, because RLS has no write policy on it by design.
 */
/**
 * Origin the invite link should point back at, matching the `origin` the claim and sponsor-invite
 * flows already send. Without it every invite links to the deployed app, so an invite raised from a
 * developer's machine could not be followed there.
 */
function currentOrigin(): string {
  return typeof window !== 'undefined' ? window.location.origin : '';
}

export const adminAccessService = {
  /**
   * A non-2xx from the function arrives as a generic FunctionsHttpError, so the readable reason is
   * recovered from the response body — see {@link extractEdgeFunctionError}. A 200 carrying an
   * `error` field is treated as a failure too.
   */
  async invokeAdminAction<T>(body: Record<string, unknown>): Promise<T> {
    const { data, error } = await supabase.functions.invoke('manage-admin-access', { body });

    if (error) {
      throw new Error(await extractEdgeFunctionError(error, 'Admin access request failed.'));
    }

    if (data?.error) throw new Error(data.error);
    return data as T;
  },

  async listAdmins(): Promise<AdminAccessRecord[]> {
    const data = await this.invokeAdminAction<{ admins: AdminAccessRecord[] }>({ action: 'list' });
    return data.admins ?? [];
  },

  /**
   * Resolves with `emailSent: false` when the admin was granted but the invite email failed. The
   * grant is real at that point, so this is reported to the superadmin as a warning rather than
   * thrown — telling them it failed outright would be wrong, and they can resend.
   */
  async inviteAdmin(email: string): Promise<{
    admin: AdminAccessRecord;
    emailSent: boolean;
    emailError: string | null;
  }> {
    return this.invokeAdminAction({ action: 'invite', email, origin: currentOrigin() });
  },

  async resendInvite(email: string): Promise<{ emailSent: boolean }> {
    return this.invokeAdminAction({ action: 'resend', email, origin: currentOrigin() });
  },

  async removeAdmin(email: string): Promise<{ removed: string }> {
    return this.invokeAdminAction({ action: 'remove', email });
  },
};
